import {NotificationIcon} from '../icons/AppIcons';
import {CalendarIcon as CalendarDays,TeamMembersIcon as Users} from '../icons/AppIcons';
// src/components/Calendar/CalendarAvailability.js
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useId } from 'react';
import {createPortal} from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {ExternalLink,Palette,ArrowDownToLine,Copy,CalendarOff,Undo2,ChevronDown,CircleHelp,Rows3,LoaderCircle} from 'lucide-react';
import AvailabilityDisplayViews from './AvailabilityDisplayViews';
import CalendarDayHeading from './CalendarDayHeading';
import {teamRoleColor,teamRoleLabel} from '../../utils/teamRoleDisplay';
import {calendarDisplayDays} from './calendarDisplay';
import AvailabilityOverlapTime from './AvailabilityOverlapTime';
import {buildDailySummaries,membersAvailableThroughout} from './availabilityOverlaps';
import {
  getFirestore,
  doc,
  getDoc,
  onSnapshot,
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

import { getTimeZoneOptions } from '../../utils/timeZoneOptions';
import { expandRecurringAvailabilities } from './_availabilityShared';
import {availabilitySubmission} from '../../shared/availability';
import { expandRecurring } from './recurrence';
import './CalendarAvailability.css';
import { getEventTagColor } from './eventTags';
import { bookingParticipantUids, subtractBookings,availabilityDayLanes,bookingLanes } from './availabilityBookings';
import {subscribeAvailabilityUsernames} from './availabilityUsernames';
import { DateTime } from "luxon";

import CustomAlert from "../core-components/CustomAlert";
export {buildDailySummaries} from './availabilityOverlaps';

const ROLE_ORDER = ['player', 'coach', 'analyst', 'sub', 'manager', 'spectator', 'trial'];

// normalize "Manager/Manger" etc → kebab-lower keys used in CSS
function normalizeRoleKey(raw) {
  const k = String((typeof raw==='object'?raw?.role:raw) || 'player').toLowerCase().replace(/\s+/g, '-');
  if (k === 'manger') return 'manager';
  if (k === 'subs') return 'sub';
  if (k === 'trialing' || k === 'trials') return 'trial';
  return k;
}

// minutes → "HH:MM"
function mmToHHMM(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

const toZonedDate = (iso, timeZone) =>
  new Date(new Date(iso).toLocaleString("en-US", { timeZone }));

function mergeIntervalsMs(intervals) {
  const sorted = (intervals || [])
    .map(([s, e]) => [Number(s), Number(e)])
    .filter(([s, e]) => Number.isFinite(s) && Number.isFinite(e) && e > s)
    .sort((a, b) => a[0] - b[0]);

  if (!sorted.length) return [];

  const out = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const [s, e] = sorted[i];
    const last = out[out.length - 1];
    if (s <= last[1]) last[1] = Math.max(last[1], e);
    else out.push([s, e]);
  }
  return out;
}

function getWeekWindowUTC(weekStart, tz) {
  // Interpret the *displayed week* in the user's active TZ,
  // then convert boundaries to UTC instants.
  const base = DateTime.fromJSDate(weekStart).setZone(tz);

  // Snap to Monday 00:00 in that TZ (Luxon weekday: Mon=1..Sun=7)
  const weekStartLocal = base.minus({ days: base.weekday - 1 }).startOf("day");
  const weekEndLocalExclusive = weekStartLocal.plus({ days: 7 }); // next Monday 00:00

  return {
    weekStartUTCms: weekStartLocal.toUTC().toMillis(),
    weekEndUTCmsExclusive: weekEndLocalExclusive.toUTC().toMillis(),
  };
}

function getDisplayWeekStartLocal(weekStart, tz) {
  const base = DateTime.fromJSDate(weekStart).setZone(tz);
  return base.minus({ days: base.weekday - 1 }).startOf("day");
}

function getDisplayWeekBounds(weekStart, tz) {
  const weekStartLocal = getDisplayWeekStartLocal(weekStart, tz);
  const weekEndLocalExclusive = weekStartLocal.plus({ days: 7 });

  return {
    weekStartLocal,
    weekEndLocalExclusive,
    weekEndLocalInclusive: weekEndLocalExclusive.minus({ milliseconds: 1 }),
  };
}

function splitIntervalIntoDisplayWeekSegments(startUTC, endUTC, weekStart, tz) {
  const startLocal = DateTime.fromISO(startUTC, { zone: "utc" }).setZone(tz);
  const endLocal = DateTime.fromISO(endUTC, { zone: "utc" }).setZone(tz);

  if (!startLocal.isValid || !endLocal.isValid || endLocal <= startLocal) {
    return [];
  }

  const weekStartLocal = getDisplayWeekStartLocal(weekStart, tz);
  const weekEndLocal = weekStartLocal.plus({ days: 7 });

  const clippedStart = startLocal < weekStartLocal ? weekStartLocal : startLocal;
  const clippedEnd = endLocal > weekEndLocal ? weekEndLocal : endLocal;

  if (clippedEnd <= clippedStart) {
    return [];
  }

  const segments = [];
  let cursor = clippedStart;

  while (cursor < clippedEnd) {
    const dayStart = cursor.startOf("day");
    const nextDayStart = dayStart.plus({ days: 1 });
    const segEnd = clippedEnd < nextDayStart ? clippedEnd : nextDayStart;

    const dayOffset = Math.floor(dayStart.diff(weekStartLocal, "days").days);
    if (dayOffset >= 0 && dayOffset <= 6) {
      const startMin = Math.round(cursor.diff(dayStart, "minutes").minutes);
      const endMin = Math.round(segEnd.diff(dayStart, "minutes").minutes);

      segments.push({
        day: dayOffset,
        startMin,
        endMin,
      });
    }

    cursor = segEnd;
  }

  return segments;
}

const isEmail = (value) =>
  /\S+@\S+\.\S+/.test((value || '').trim());

const safe = (value) => {
  const next = (value || '').trim();
  return isEmail(next) ? '' : next;
};

const redactEmail = (value) =>
  /\S+@\S+\.\S+/.test(value || '')
    ? 'Member'
    : value || 'Member';

const getAvailCollectionRef = (db, { mode, teamId, uid, availabilityTeamId }) => {
    if (availabilityTeamId) return collection(db, 'teams', availabilityTeamId, 'availabilities');
    if (mode === 'personal') {
      if (!uid) return null;
      return collection(db, 'users', uid, 'personalAvailabilities');
    }
    if (!teamId) return null;
    return collection(db, 'teams', teamId, 'availabilities');
  };


export default function CalendarAvailability({
  mode = 'team', // "team" | "personal"
  teamId,
  weekStart, // Date – Monday of the viewed week
  selectedTimeZone,
  onPrevWeek,
  onNextWeek,
  onToday,
  onBackToCalendar,
  onTimeZoneChange,
  onImportFromPersonal,
  onRequestMemberAvailability,
  onToggleUnavailableThisWeek,
  embedded = false,
  planningOnly = false, // Overview Team Availability schedules events instead of editing submissions.
  display = 'detailed',
  displayDate,
  onSwitchMode,
  onImportAllTeams,
  onCreateCalendarEvent,
  renderScheduledEvent,
  renderScheduledSummary,
  onPreviewCalendarEvent,
  toolsPortal,
  helpPortal,
  showHelp = true,
  availabilityTeamId = null, // Own team submissions, using Personal Availability's UI.
  addButtonRef,
  readOnly = false,
  readOnlyMessage = "Your active team can view availability, but editing is available once the team is on Basic or higher.",
}) {
  const navigate = useNavigate();
  const inlineMembers=planningOnly&&!!toolsPortal;
  const eventSelectionRef=useRef(null);

  const auth = getAuth();
  const currentUid = auth.currentUser?.uid;

  // ---------- Firestore subcollection helpers ----------
  // ---------- State ----------
  const [userColors, setUserColors] = useState({});
  const [members, setMembers] = useState([]);
  const [availByMember, setAvailByMember] = useState({});
  // busyByUid = "time booked on OTHER teams" for this displayed week
  const [busyByUid, setBusyByUid] = useState({}); // { [uid]: Array<[startMs,endMs]> }



  // raw list from Firestore subcollection docs (base records)
  const [rawFirestoreList, setRawFirestoreList] = useState([]);
  // expanded list (includes recurring instances)
  const [rawAvails, setRawAvails] = useState([]);
  const [editing, setEditing] = useState(null);

  // dragging / selection
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null); // { day, minutes }
  const [dragEnd, setDragEnd] = useState(null);
  const [selectionRange, setSelectionRange] = useState(null);

  // popup
  const [popup, setPopup] = useState(null);
  const [popupPos, setPopupPos] = useState({ x: 0, y: 0 });

  // Hover tooltip
  const [hoveredUser, setHoveredUser] = useState(null);

  // ✅ Request availability feedback (sender confirmation)
  const [requestAlert, setRequestAlert] = useState(null); // { type: "success"|"error"|"info", message: string }
  const [requestSendingUid, setRequestSendingUid] = useState(null);
  const [authActionAlert, setAuthActionAlert] = useState("");

  // menus/refs
  const containerRef = useRef();
  const popupRef = useRef();
  const membersMenuRef = useRef();
  const membersButtonRef = useRef(null);
  const membersMenuId=useId();
  const [membersMenuPosition,setMembersMenuPosition]=useState(null);
  const recurMenuRef = useRef();
  const helpRef = useRef(null);

  // team meta (roles/members/maps/displayName)
  const [teamData, setTeamData] = useState({});
  const [showTeamEvents, setShowTeamEvents] = useState(true);
  const [bookingDetails, setBookingDetails] = useState(null);
  const bookingCloseTimer = useRef(null);
  const keepBookingDetailsOpen = () => {
    clearTimeout(bookingCloseTimer.current);
    bookingCloseTimer.current = null;
  };
  const scheduleBookingDetailsClose = () => {
    keepBookingDetailsOpen();
    // Bridge the small gap between the event and its popover.
    bookingCloseTimer.current = setTimeout(() => {
      if(renderScheduledEvent&&document.querySelector('dialog[open],.overview-participants-popover'))return;
      setBookingDetails(null);
    }, 200);
  };
  const blurBookingDetails = event => {
    if (!event.relatedTarget?.closest?.('.availability-booking-label, .availability-booking-popover, dialog, .overview-participants-popover')) {
      scheduleBookingDetailsClose();
    }
  };
  useEffect(() => () => clearTimeout(bookingCloseTimer.current), []);
  useEffect(() => {
    clearTimeout(bookingCloseTimer.current);
    setBookingDetails(null);
  }, [teamId, weekStart, selectedTimeZone]);
  useEffect(() => {
    if (!bookingDetails) return undefined;
    const dismiss = event => {
      if(renderScheduledEvent&&document.querySelector('dialog[open]'))return;
      if (event.type === 'keydown' && event.key !== 'Escape') return;
      if (event.type === 'pointerdown' && event.target.closest('.availability-booking-label, .availability-booking-popover, .overview-participants-popover')) return;
      setBookingDetails(null);
    };
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', dismiss);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      document.removeEventListener('keydown', dismiss);
    };
  }, [bookingDetails,renderScheduledEvent]);
  const teamDataRef = useRef({});
  useEffect(() => {
    teamDataRef.current = teamData || {};
  }, [teamData]);

  // cache uid → display name for this session
  const nameCacheRef = useRef(new Map());
  const [liveUsernamesByUid, setLiveUsernamesByUid] = useState({});
  const liveUsernamesByUidRef = useRef({});
  // ✅ IMPORTANT: when switching teams, clear stale cached team/meta so we never mix rosters
  useEffect(() => {
    if (mode !== 'team') return;

    // clear the ref immediately so the availability snapshot cannot use old team members
    teamDataRef.current = {};

    // clear UI + computed state so Members… menu can't show the old team while new data loads
    setTeamData({});
    setTeamRoles({});
    setMembers([]);
    setAvailByMember({});
    setRawFirestoreList([]);
    setRawAvails([]);
    setEditing(null);
    setPopup(null);
    setSelectionRange(null);
    setShowRoleMenu(false);
    setBusyByUid({});


    // clear name cache so we re-resolve display names cleanly for this team session
    nameCacheRef.current = new Map();
    setLiveUsernamesByUid({});
  }, [teamId, mode]);

  const [teamRoles, setTeamRoles] = useState({});
  const [showRoleMenu, setShowRoleMenu] = useState(false);

  const relevantIdentityUids = useMemo(() => {
    const ids = new Set();
    const add = (value) => {
      if (typeof value === "string" && value.trim()) {
        ids.add(value.trim());
      }
    };

    add(currentUid);

    const membersRaw =
      mode === "personal"
        ? [currentUid]
        : Array.isArray(teamData?.members)
        ? teamData.members
        : [];

    membersRaw.forEach((member) => {
      if (typeof member === "string") {
        add(member);
        return;
      }

      add(
        member?.uid ||
          member?.userId ||
          member?.id
      );
    });

    return Array.from(ids).sort();
  }, [
    currentUid,
    mode,
    teamData,
  ]);

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(max-width: 46rem)").matches;
  });

  const [showMobileSummary, setShowMobileSummary] = useState(true);

  useEffect(() => {
    liveUsernamesByUidRef.current =
      liveUsernamesByUid || {};
  }, [liveUsernamesByUid]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const mq = window.matchMedia("(max-width: 46rem)");
    const onChange = () => setIsMobile(mq.matches);

    setIsMobile(mq.matches);

    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // If user leaves mobile, always show summary
  useEffect(() => {
    // Summary is vital: default to visible on both desktop + mobile
    setShowMobileSummary(true);
  }, [isMobile]);


  // ---------- stable per-uid color ----------
  function hashStr(str) {
    let h = 2166136261 >>> 0; // FNV-1a base
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }
  function hslToHex(h, s, l) {
    s /= 100;
    l /= 100;
    const k = (n) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    const toHex = (x) => Math.round(x * 255).toString(16).padStart(2, '0');
    return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
  }
  function getHexColorForUid(uid) {
    const h = hashStr(String(uid)) % 360;
    const s = 68;
    const l = 54;
    return hslToHex(h, s, l);
  }

  // ---------- Persist selected members ----------
  const storageKey = useMemo(
    () => `CalendarAvailability.enabledUsers.${teamId || (mode === 'personal' ? 'personal' : 'unknown')}`,
    [teamId, mode]
  );

  const selectionExplicit=useRef(false),loadedMembersTeam=useRef(null);
  const [enabledUserIds, setEnabledUserIds] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || '[]');
      return new Set(Array.isArray(saved) ? saved : []);
    } catch {
      return new Set();
    }
  });

  // ✅ clear enabled users immediately when switching teams (after hook exists)
  useEffect(() => {
    if (mode !== 'team') return;
    loadedMembersTeam.current=null;
    setEnabledUserIds(new Set());
  }, [teamId, mode]);

  useEffect(() => {
    try{const saved=JSON.parse(localStorage.getItem(storageKey)||'null');selectionExplicit.current=Array.isArray(saved);setEnabledUserIds(new Set(Array.isArray(saved)?saved:[]));}
    catch{selectionExplicit.current=false;setEnabledUserIds(new Set());}
  },[storageKey]);
  useEffect(()=>{if(selectionExplicit.current)try{localStorage.setItem(storageKey,JSON.stringify([...enabledUserIds]));}catch{}},[enabledUserIds,storageKey]);

  // default to ONLY PLAYERS + ALWAYS include me if nothing saved
  useEffect(() => {
    if (!members.length) return;
    if (selectionExplicit.current||enabledUserIds.size !== 0) return;
    if(mode==='team'&&loadedMembersTeam.current!==teamId)return;

    // ✅ Wait until roles are loaded (team mode), otherwise everyone may look like "player"
    if (mode === "team" && (!teamRoles || Object.keys(teamRoles).length === 0)) return;

    const playerUids = members
      .filter((m) => normalizeRoleKey(teamRoles?.[m.uid] || "") === "player")
      .map((m) => m.uid);

    if (currentUid && !playerUids.includes(currentUid)) {
      playerUids.push(currentUid);
    }

    selectionExplicit.current=true;
    setEnabledUserIds(new Set(playerUids));
  }, [members, teamRoles, currentUid, mode, enabledUserIds,teamId]);

  const toggleMemberGroup=ids=>{
    selectionExplicit.current=true;
    setEnabledUserIds(previous=>{const remove=ids.length>0&&ids.every(id=>previous.has(id)),next=new Set(previous);ids.forEach(id=>remove?next.delete(id):next.add(id));return next;});
  };
  const toggleUser = (uid) => {
    selectionExplicit.current=true;
    setEnabledUserIds((prev) => {
      const next = new Set(prev);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });
  };

  // ---------- load my availability color ----------
  useEffect(() => {
    const db = getFirestore();
    const uid = currentUid;
    if (!uid) return;

    // Team mode: load all member colors so everyone sees the same colors
    if (mode === "team" && teamId) {
      const membersRef = collection(db, "teams", teamId, "members");

      const unsub = onSnapshot(membersRef, (snap) => {
        const nextColors = {};

        snap.forEach((memberDoc) => {
          const data = memberDoc.data() || {};
          const hex =
            typeof data.availabilityColor === "string"
              ? data.availabilityColor.trim()
              : "";

          if (/^#([0-9a-f]{6})$/i.test(hex)) {
            nextColors[memberDoc.id] = hex;
          }
        });

        setUserColors(nextColors);
      });

      return () => unsub();
    }

    // Personal mode fallback: keep old behavior for My Availability
    (async () => {
      try {
        const uSnap = await getDoc(doc(db, "users", uid));
        const data = uSnap.exists() ? uSnap.data() || {} : {};
        const hex =
          typeof data.availabilityColor === "string"
            ? data.availabilityColor.trim()
            : "";

        if (/^#([0-9a-f]{6})$/i.test(hex)) {
          setUserColors((prev) => ({ ...prev, [uid]: hex }));
        }
      } catch {
        // ignore
      }
    })();
  }, [currentUid, mode, teamId]);

  const handleSaveMyColor = async (hex) => {
    if (!/^#([0-9a-f]{6})$/i.test(hex || "")) return;

    const uid = getAuth().currentUser?.uid;
    if (!uid) return;

    // optimistic update
    setUserColors((prev) => ({ ...prev, [uid]: hex }));

    try {
      const db = getFirestore();

      if (mode === "team" && teamId) {
        await setDoc(
          doc(db, "teams", teamId, "members", uid),
          {
            availabilityColor: hex,
          },
          { merge: true }
        );
      } else {
        await updateDoc(doc(db, "users", uid), {
          availabilityColor: hex,
        });
      }
    } catch (e) {
      console.error("Failed to save availabilityColor", e);
    }
  };
  // ---------- keep team meta in sync (members, roles, name maps) ----------
  useEffect(() => {
    const db = getFirestore();
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;

    const ref =
      availabilityTeamId ? doc(db, 'teams', availabilityTeamId) : mode === 'personal' ? doc(db, 'users', uid) : teamId ? doc(db, 'teams', teamId) : null;
    if (!ref) return;

    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.data() || {};

      // ✅ keep ref in sync immediately (prevents timing window where avail snapshot uses stale meta)
      teamDataRef.current = data;

      loadedMembersTeam.current=teamId;
      setTeamData(data);
      setTeamRoles(data.teamRoles || {});
    });

    return () => unsub();
  }, [mode, teamId, currentUid, availabilityTeamId]);

  useEffect(() => {
    if (!relevantIdentityUids.length) {
      setLiveUsernamesByUid({});
      return undefined;
    }

    const db = getFirestore();
    return subscribeAvailabilityUsernames({db, currentUid, userIds: relevantIdentityUids,
      onName: (userId, username) => {
        const nextUsername = safe(username);

        setLiveUsernamesByUid((previous) => {
          const current = previous[userId] || "";

          if (current === nextUsername) {
            return previous;
          }

          if (!nextUsername) {
            if (!(userId in previous)) {
              return previous;
            }

            const next = { ...previous };
            delete next[userId];
            return next;
          }

          return {
            ...previous,
            [userId]: nextUsername,
          };
        });
      },
      onError: error => console.warn("CalendarAvailability: username lookup unavailable; using roster names.", error),
    });
  }, [relevantIdentityUids, currentUid]);

  useEffect(() => {
    if (!Object.keys(liveUsernamesByUid).length) {
      return;
    }

    setMembers((previous) =>
      previous.map((member) => {
        const nextName = safe(
          liveUsernamesByUid?.[
            member?.uid
          ]
        );

        if (
          !nextName ||
          nextName === member?.name
        ) {
          return member;
        }

        return {
          ...member,
          name: nextName,
        };
      })
    );
  }, [liveUsernamesByUid]);

  // ---------- Compute busy time from OTHER teams' scheduled events ----------
  useEffect(() => {
    if (mode !== "team") {
      setBusyByUid({});
      return;
    }
    if (!currentUid || !teamId || !weekStart) return;

    const db = getFirestore();

    let { weekStartLocal, weekEndLocalInclusive } = getDisplayWeekBounds(
      weekStart,
      selectedTimeZone
    );

    if(display==='month'&&displayDate){const range=calendarDisplayDays(displayDate,selectedTimeZone,'month');weekStartLocal=range[0];weekEndLocalInclusive=range.at(-1).endOf('day');}
    let alive=true;
    const gatherUserTeamIds = (userData = {}, activeTeamId) => {
      const ids = new Set();
      const add = (x) => { if (typeof x === "string" && x.trim()) ids.add(x.trim()); };

      (userData.teamIds || []).forEach(add);
      (userData.teams || []).forEach(t => add(typeof t === "string" ? t : (t?.id || t?.teamId)));
      (userData.memberships || []).forEach(m => add(typeof m === "string" ? m : (m?.id || m?.teamId)));

      if (userData.teamsMap && typeof userData.teamsMap === "object") {
        Object.keys(userData.teamsMap).forEach(add);
      }

      if (activeTeamId) add(activeTeamId);
      return Array.from(ids);
    };

    (async () => {
      try {
        // 1) load user -> all team ids
        const uSnap = await getDoc(doc(db, "users", currentUid));
        const userData = uSnap.exists() ? (uSnap.data() || {}) : {};
        const teamIds = gatherUserTeamIds(userData, teamId);

        // 2) load each team doc (must be member per your rules)
        const teamSnaps = await Promise.all(teamIds.map(async (tid) => {
          try { return { tid, snap: await getDoc(doc(db, "teams", tid)) }; }
          catch { return { tid, snap: null }; }
        }));

        // 3) build busy intervals per uid from OTHER teams' events
        const busy = {}; // uid -> [[s,e],...]

        for (const { tid, snap } of teamSnaps) {
          if (!snap || !snap.exists()) continue;
          if (tid === teamId) continue; // important: OTHER teams only

          const data = snap.data() || {};
          const events = Array.isArray(data.events) ? data.events : [];

          // expand recurring events into THIS week window
          const expandedEvents = expandRecurring(
            events,
            weekStartLocal.toJSDate(),
            weekEndLocalInclusive.toJSDate()
          );

          for (const ev of expandedEvents) {
            const s = Date.parse(ev.startUTC);
            const e = Date.parse(ev.endUTC);
            if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) continue;

            const uids = bookingParticipantUids(ev,[]);
            for (const uid of uids) {
              (busy[uid] ||= []).push([s, e]);
            }
          }
        }

        // normalize / merge
        Object.keys(busy).forEach(uid => {
          busy[uid] = mergeIntervalsMs(busy[uid]);
        });

        if(alive)setBusyByUid(busy);
      } catch (e) {
        console.warn("Failed to compute cross-team busy time:", e);
        if(alive)setBusyByUid({});
      }
    })();
    return()=>{alive=false;};
  }, [mode, currentUid, teamId, weekStart, selectedTimeZone, display, displayDate]);

  // ---------- (1) Listen to availability subcollection: LOAD ONLY ----------
  useEffect(() => {
    const db = getFirestore();
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;

    const availCol = getAvailCollectionRef(db, { mode, teamId, uid, availabilityTeamId });
    if (!availCol) return;

    const unsub = onSnapshot(
      availCol,
      (snap) => {
        try {
          // docs → records (must include id)
          const avails = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
            .filter(record=>!availabilityTeamId||(record.userId||record.uid||record.userUid||record.createdByUid)===uid);
          setRawFirestoreList(avails);

          // compute week range
          // compute visible week range in the SELECTED TIME ZONE
          const { weekStartLocal, weekEndLocalInclusive } = getDisplayWeekBounds(
            weekStart,
            selectedTimeZone
          );

          const currentWeekKey = getDisplayWeekStartLocal(weekStart, selectedTimeZone).toISODate();

          const unavailableUids = new Set(
            avails
              .filter(
                (a) =>
                  a?.unavailableAllWeek === true &&
                  a?.submissionWeekKey === currentWeekKey
              )
              .map((a) => a.userId)
              .filter(Boolean)
          );

          // Exclude:
          // 1) the unavailable-all-week records themselves
          // 2) normal availability docs belonging to those unavailable users
          const schedulableAvails = avails.filter(
            (a) => !a?.unavailableAllWeek && !unavailableUids.has(a.userId)
          );

          // expand recurring into visible week
          const expanded = expandRecurringAvailabilities(
            schedulableAvails,
            weekStartLocal.toJSDate(),
            weekEndLocalInclusive.toJSDate()
          );
          setRawAvails(expanded);
        } catch (err) {
          console.error(err);
        }
      },
      (err) => console.error(err)
    );

    return () => unsub();
  }, [mode, teamId, weekStart, selectedTimeZone, currentUid, availabilityTeamId]);

  // ---------- (2) Display saved availability without subtracting calendar bookings ----------
  useEffect(() => {
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;

    try {
      const teamMeta = teamDataRef.current || {};
      const byUser = {};
      // Availability documents can outlive membership (including recurring series).
      // Only the live roster may supply members, never the saved availability.
      const rosterIds = mode === 'personal' ? [uid] :
        (Array.isArray(teamMeta.members) ? teamMeta.members : [])
          .map(member => typeof member === 'string' ? member : member?.uid || member?.userId || member?.id)
          .filter(Boolean);
      const allUids = new Set(rosterIds);

      // Build saved availability; booking conflicts belong only to bookable suggestions.
      (rawAvails || []).forEach((a) => {
        if (!allUids.has(a.userId)) return;


        const baseStartMs = Date.parse(a.startUTC);
        const baseEndMs = Date.parse(a.endUTC);
        if (!Number.isFinite(baseStartMs) || !Number.isFinite(baseEndMs) || baseEndMs <= baseStartMs) return;

        const remainingPartsMs = [[baseStartMs, baseEndMs]];
        if (!remainingPartsMs.length) return;

        if (!byUser[a.userId]) {
          byUser[a.userId] = { name: safe(a.username) || "", events: [] };
        }

        const segmentMeta = {
          sourceId: a.id,
          startUTC: a.startUTC,
          endUTC: a.endUTC,
          isRecurringInstance: !!a.isRecurringInstance,
          instanceDate: a.instanceDate || null,
        };

        for (const [partStartMs, partEndMs] of remainingPartsMs) {
          const partStartISO = new Date(partStartMs).toISOString();
          const partEndISO = new Date(partEndMs).toISOString();

          const segments = splitIntervalIntoDisplayWeekSegments(
            partStartISO,
            partEndISO,
            weekStart,
            selectedTimeZone
          );

          for (const seg of segments) {
            byUser[a.userId].events.push({
              day: seg.day,
              startMin: seg.startMin,
              endMin: seg.endMin,
              ...segmentMeta,
            });
          }
        }
      });

      // De-dupe segments per user
      for (const u of Object.keys(byUser)) {
        const seenSeg = new Set();
        byUser[u].events = byUser[u].events.filter((e) => {
          const k = `${e.sourceId || ""}|${e.instanceDate || ""}|${e.day}|${e.startMin}|${e.endMin}`;
          if (seenSeg.has(k)) return false;
          seenSeg.add(k);
          return true;
        });
      }

      // roster base
      const teamMembersRaw =
        mode === "personal"
          ? [
              {
                uid,
                name:
                  liveUsernamesByUidRef.current?.[
                    uid
                  ] ||
                  teamMeta.username ||
                  teamMeta.displayName ||
                  "Me",
              },
            ]
          : Array.isArray(teamMeta.members)
          ? teamMeta.members
          : [];

      teamMembersRaw.forEach((m) => {
        const mid = typeof m === "string" ? m : m?.uid || m?.userId || m?.id;
        if (mid) allUids.add(mid);
      });

      // Resolve names (same logic you had)
      const uidToName = {};

      const resolveAll = () => {
          [...allUids].forEach((u) => {
            const fromLiveProfile =
              safe(
                liveUsernamesByUidRef.current?.[u]
              );

            if (fromLiveProfile) {
              nameCacheRef.current.set(
                u,
                fromLiveProfile
              );
              uidToName[u] =
                fromLiveProfile;
              return;
            }

            if (nameCacheRef.current.has(u)) {
              uidToName[u] = nameCacheRef.current.get(u);
              return;
            }

            const fromByUser = safe(byUser[u]?.name);
            let fromRosterObj = "";
            const ro = teamMembersRaw.find(
              (m) => typeof m === "object" && (m?.uid || m?.userId || m?.id) === u
            );
            if (ro) {
              fromRosterObj = safe(ro.username) || safe(ro.displayName) || safe(ro.name);
            }

            const fromTeamMaps = safe(teamMeta?.usernames?.[u]) || safe(teamMeta?.displayNames?.[u]);

            let resolved = fromRosterObj || fromTeamMaps || fromByUser || "";
            if (resolved === u) resolved = "";

            // Render immediately; the identity listeners fill missing names live.
            if (!resolved || resolved === u) resolved = "Member";
            nameCacheRef.current.set(u, resolved);
            uidToName[u] = resolved;
          });

        const roster =
          mode === "personal"
            ? [{ uid, name: uidToName[uid] || "Me" }]
            : rosterIds.map((u) => ({ uid: u, name: uidToName[u] || "Member" }));

        const availByMemberFull = Object.fromEntries(
          roster.map((m) => [m.uid, byUser[m.uid]?.events || []])
        );

        setMembers(roster);
        setAvailByMember(availByMemberFull);
      };

      resolveAll();
    } catch (err) {
      console.error(err);
    }
  }, [
    rawAvails,
    mode,
    weekStart,
    selectedTimeZone,
    currentUid,
    teamData,           // ✅ ensures roster refresh when team meta updates
  ]);

  // ---------- findBase by doc id (IMPORTANT) ----------
  const findBase = (inst) => {
    if (!inst?.id) return null;
    return rawFirestoreList.find((a) => a.id === inst.id) || null;
  };

  // ---------- Helpers for popup time display ----------
  const popupUTC = date => DateTime.fromObject({year:date.getFullYear(),month:date.getMonth()+1,day:date.getDate(),hour:date.getHours(),minute:date.getMinutes()},{zone:selectedTimeZone}).toUTC().toISO();
  const fmt = (d) => {
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  };

  const fmtEnd = (startDate, endDate) => {
    if (!startDate || !endDate) return '00:00';

    const isNextDayMidnight =
      endDate.getHours() === 0 &&
      endDate.getMinutes() === 0 &&
      (endDate.getFullYear() !== startDate.getFullYear() ||
        endDate.getMonth() !== startDate.getMonth() ||
        endDate.getDate() !== startDate.getDate());

    return isNextDayMidnight ? '24:00' : fmt(endDate);
  };

  const getPopupRangeForDay = (popupObj) => {
    if (!popupObj?.startDate || !popupObj?.endDate) return null;

    const start = popupObj.startDate;
    const end = popupObj.endDate;

    const from = start.getHours() * 60 + start.getMinutes();

    const sameDay =
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDate() === end.getDate();

    const to = sameDay ? end.getHours() * 60 + end.getMinutes() : 24 * 60;

    return { day: popupObj.day, from, to };
  };

  const getDayLabel = (i) => {
    return getDisplayWeekStartLocal(weekStart,selectedTimeZone).plus({days:i}).toFormat('LLL d');
  };

  const toISODate = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const defaultRepeatEndDate4Months = (startDate) => {
    const d = new Date(startDate);
    d.setMonth(d.getMonth() + 4);
    return toISODate(d);
  };

  const generateTimeOptions = (include24 = false) => {
    const opts = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        const hh = String(h).padStart(2, '0');
        const mm = String(m).padStart(2, '0');
        opts.push(`${hh}:${mm}`);
      }
    }
    if (include24) opts.push('24:00');
    return opts;
  };

  const handleEditAvailabilityBySource = (evtMeta, day, hour, clickEvent) => {
    if(planningOnly)return;
    if (readOnly) {
      showReadOnlyAlert();
      return;
    }

    if (!evtMeta?.sourceId) return;
    if (!currentUid) return;

    // Find the expanded record with the same id (and instanceDate if recurring instance)
    const rec = evtMeta.record?.userId===currentUid ? evtMeta.record : rawAvails.find((a) => {
      if (a.userId !== currentUid) return false;
      if (a.id !== evtMeta.sourceId) return false;
      if (evtMeta.instanceDate && a.instanceDate) {
        return a.instanceDate === evtMeta.instanceDate;
      }
      return true;
    });

    if (!rec) return;

    setEditing(rec);

    // ✅ If it’s a recurring instance, we still need to position the recurring-action-menu
    if (rec.isRecurringInstance && rec.repeat && rec.repeat !== 'none') {
      if (containerRef.current && clickEvent?.clientX != null && clickEvent?.clientY != null) {
        const rect = containerRef.current.getBoundingClientRect();
        const x = Math.min(clickEvent.clientX - rect.left, rect.width - 15 * 16 - 10);
        const y = Math.min(clickEvent.clientY - rect.top, rect.height - 15 * 16 - 10);
        setPopupPos({ x, y });
      }
      return;
    }

    // non-recurring => open edit popup
    const startD = toZonedDate(rec.startUTC, selectedTimeZone);
    const endD = toZonedDate(rec.endUTC, selectedTimeZone);

    if (containerRef.current && clickEvent?.clientX != null && clickEvent?.clientY != null) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = Math.min(clickEvent.clientX - rect.left, rect.width - 15 * 16 - 10);
      const y = Math.min(clickEvent.clientY - rect.top, rect.height - 15 * 16 - 10);
      setPopupPos({ x, y });
    }

    setPopup({
      day,
      startDate: startD,
      endDate: endD,
      repeat: rec.repeat || "none",
      repeatEndDate:
        rec.repeat && rec.repeat !== "none"
          ? rec.repeatEndDate || defaultRepeatEndDate4Months(startD)
          : "",
    });
  };

  // ---------- Drag handlers ----------
  const handleMouseDown = (day, hour, e) => {
    if (readOnly) {
      showReadOnlyAlert();
      return;
    }

    e.preventDefault();
    const { top, height } = e.currentTarget.getBoundingClientRect();
    const yPct = (e.clientY - top) / height;
    const mins = Math.floor((yPct * 60) / 15) * 15;
    setDragStart({ day, minutes: hour * 60 + mins });
    setDragEnd({ day, minutes: hour * 60 + mins });
    setIsDragging(true);
  };

  const handleMouseMove = (day, hour, e) => {
    if (!isDragging||day!==dragStart?.day) return;
    const { top, height } = e.currentTarget.getBoundingClientRect();
    const yPct = (e.clientY - top) / height;
    const mins = Math.floor((yPct * 60) / 15) * 15;
    setDragEnd({ day, minutes: hour * 60 + mins });
  };

  const handleSelectEntireDay = (day, e, explicitDate) => {
    if(planningOnly)return;
    if (readOnly) {
      showReadOnlyAlert();
      return;
    }

    if (editing) return;

    const dayDate = explicitDate || getDisplayWeekStartLocal(weekStart, selectedTimeZone).plus({days:day});
    const startDate = new Date(dayDate.year, dayDate.month-1, dayDate.day);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 1);
    endDate.setHours(0, 0, 0, 0);

    setIsDragging(false);
    setDragStart(null);
    setDragEnd(null);
    setEditing(null);

    setSelectionRange({
      day,
      from: 0,
      to: 24 * 60,
    });

    if (containerRef.current && e?.currentTarget) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const headerRect = e.currentTarget.getBoundingClientRect();

      const popupWidth = 220;
      const popupHeight = 320;

      const x = Math.min(
        Math.max(headerRect.left - containerRect.left, 0),
        containerRect.width - popupWidth - 10
      );

      const y = Math.min(
        Math.max(headerRect.bottom - containerRect.top + 8, 0),
        containerRect.height - popupHeight - 10
      );

      setPopupPos({ x, y });
    }

    setPopup({
      day,
      startDate,
      endDate,
      repeat: "none",
      repeatEndDate: "",
    });
  };

  useEffect(() => {
    const onMouseUp = (e) => {
      if (!isDragging || !dragStart || !dragEnd) {
        setIsDragging(false);
        return;
      }
      setIsDragging(false);

      const [a, b] = [dragStart.minutes, dragEnd.minutes].sort((x, y) => x - y);
      const startMin = a;
      const endMin = Math.min(1440,b + 15);
      if(planningOnly){eventSelectionRef.current?.(dragStart.day,startMin,endMin);setSelectionRange(null);return;}

      const dayDate=getDisplayWeekStartLocal(weekStart,selectedTimeZone).plus({days:dragStart.day});
      const startDate = new Date(dayDate.year,dayDate.month-1,dayDate.day);
      startDate.setHours(Math.floor(startMin / 60));
      startDate.setMinutes(startMin % 60);

      const endDate = new Date(dayDate.year,dayDate.month-1,dayDate.day);
      endDate.setHours(Math.floor(endMin / 60)); // 24 rolls to next day
      endDate.setMinutes(endMin % 60);

      setSelectionRange({ day: dragStart.day, from: startMin, to: endMin });

      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const x = Math.min(e.clientX - rect.left, rect.width - 280 - 10);
        const y = Math.min(e.clientY - rect.top, rect.height - 360 - 10);
        setPopupPos({ x, y });
      }

      setPopup({
        day: dragStart.day,
        startDate,
        endDate,
        repeat: 'none',
        repeatEndDate: '',
      });
    };

    document.addEventListener('mouseup', onMouseUp);
    return () => document.removeEventListener('mouseup', onMouseUp);
  }, [isDragging, dragStart, dragEnd, weekStart, selectedTimeZone,planningOnly]);

  // ---------- Close popups when clicking outside ----------
  const handleCancel = () => {
    setPopup(null);
    setSelectionRange(null);
    setEditing(null);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (popup && popupRef.current && !popupRef.current.contains(event.target)) {
        handleCancel();
      }
      if (
        editing?.isRecurringInstance &&
        !popup?.isOverride &&
        !popup?.isSplitFuture &&
        recurMenuRef.current &&
        !recurMenuRef.current.contains(event.target)
      ) {
        setEditing(null);
        setPopup(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [popup, editing]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (showRoleMenu && membersMenuRef.current && !membersMenuRef.current.contains(event.target) && !membersButtonRef.current?.contains(event.target)) {
        setShowRoleMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showRoleMenu]);

  useLayoutEffect(()=>{
    if(!embedded||!showRoleMenu||inlineMembers)return undefined;
    const place=()=>{
      if(!membersButtonRef.current||!membersMenuRef.current)return;
      const anchor=membersButtonRef.current.getBoundingClientRect(),box=membersMenuRef.current.getBoundingClientRect();
      const left=Math.max(8,Math.min(anchor.left,window.innerWidth-box.width-8));
      const top=Math.max(8,anchor.bottom+8+box.height<=window.innerHeight-8?anchor.bottom+8:anchor.top-box.height-8);
      setMembersMenuPosition(previous=>previous?.left===left&&previous?.top===top?previous:{left,top});
    };
    const escape=event=>{if(event.key==='Escape'){event.preventDefault();setShowRoleMenu(false);membersButtonRef.current?.focus();}};
    place();window.addEventListener('resize',place);window.addEventListener('scroll',place,true);document.addEventListener('keydown',escape);
    return()=>{window.removeEventListener('resize',place);window.removeEventListener('scroll',place,true);document.removeEventListener('keydown',escape);};
  },[embedded,showRoleMenu,members,inlineMembers]);

  // ---------- Confirm (create/edit/override/split-future) using DOCS ----------
  const handleConfirm = async () => {
    if (readOnly) {
      showReadOnlyAlert();
      return;
    }

    const db = getFirestore();
    const uid = getAuth().currentUser?.uid;

    if (!uid) {
      showLoginRequiredAlert("Please Login to use this function");
      return;
    }

    if (!popup?.startDate || !popup?.endDate) return;

    const availCol = getAvailCollectionRef(db, { mode, teamId, uid, availabilityTeamId });
    if (!availCol) return;

    // (A) Edit this instance (override)
    if (popup?.isOverride && editing) {
      const base = findBase(editing);
      if (!base?.id || !editing.instanceDate) {
        setEditing(null);
        setPopup(null);
        return;
      }

      const ref = doc(availCol, base.id);
      const key = editing.instanceDate;

      await updateDoc(ref, {
        [`overrides.${key}`]: {
          startUTC: popupUTC(popup.startDate),
          endUTC: popupUTC(popup.endDate),
        },
      });

      setEditing(null);
      setPopup(null);
      setSelectionRange(null);
      return;
    }

    // (B) Edit this and all future (truncate old series + create a new one)
    if (popup?.isSplitFuture && editing) {
      const base = findBase(editing);
      if (!base?.id || !editing.instanceDate) {
        setEditing(null);
        setPopup(null);
        return;
      }

      // cutoff is the week BEFORE this instance (same logic you used)
      const [y, m, d] = editing.instanceDate.split('-').map(Number);
      const cutoff = new Date(y, m - 1, d);
      const back = editing.repeat === 'biweekly' ? 14 : 7;
      cutoff.setDate(cutoff.getDate() - back);
      const repeatEndDate = cutoff.toISOString().slice(0, 10);

      // 1) truncate old series
      await updateDoc(doc(availCol, base.id), {
        repeatEndDate,
      });

      // 2) create new future series doc
      const isProbablyEmail = (s) => typeof s === 'string' && /\S+@\S+\.\S+/.test(s);
      const safeUsername = isProbablyEmail(base.username) ? 'Member' : base.username || 'Member';

      await addDoc(availCol, {
        userId: base.userId,
        username: safeUsername,
        startUTC: popupUTC(popup.startDate),
        endUTC: popupUTC(popup.endDate),
        repeat: popup.repeat,
        repeatEndDate: popup.repeat !== 'none' ? popup.repeatEndDate || null : null,
        exceptions: [],
        overrides: {},
        createdAt: Date.now(),
      });

      setEditing(null);
      setPopup(null);
      setSelectionRange(null);
      return;
    }

    // (C) Normal edit (update the doc)
    if (editing) {
      const base = findBase(editing) || editing;
      if (!base?.id) {
        setEditing(null);
        setPopup(null);
        return;
      }

      await updateDoc(doc(availCol, base.id), {
        startUTC: popupUTC(popup.startDate),
        endUTC: popupUTC(popup.endDate),
        repeat: popup.repeat,
        repeatEndDate: popup.repeat !== 'none' ? popup.repeatEndDate || null : null,
      });

      setEditing(null);
      setPopup(null);
      setSelectionRange(null);
      return;
    }

    // (D) Create new doc
    let username = 'Member';
    try {
      const uSnap = await getDoc(doc(db, 'users', uid));
      if (uSnap.exists()) {
        const ud = uSnap.data() || {};
        username = safe(ud.username) || safe(ud.displayName) || safe(ud.name) || 'Member';
      }
    } catch {}

    await addDoc(availCol, {
      userId: uid,
      username,
      startUTC: popupUTC(popup.startDate),
      endUTC: popupUTC(popup.endDate),
      repeat: popup.repeat,
      repeatEndDate: popup.repeat !== 'none' ? popup.repeatEndDate || null : null,
      exceptions: [],
      overrides: {},
      createdAt: Date.now(),
    });

    setPopup(null);
    setSelectionRange(null);
  };

  // ---------- Delete whole record ----------
  const handleDelete = async () => {
    if (readOnly) {
      showReadOnlyAlert();
      return;
    }

    if (!editing) return;

    const db = getFirestore();
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;

    const availCol = getAvailCollectionRef(db, { mode, teamId, uid, availabilityTeamId });
    if (!availCol) return;

    const base = findBase(editing) || editing;
    if (!base?.id) return;

    await deleteDoc(doc(availCol, base.id));

    setEditing(null);
    setPopup(null);
    setSelectionRange(null);
  };

  // ---------- Delete only this instance (exception) ----------
  const handleDeleteInstance = async (rec) => {
    if (readOnly) {
      showReadOnlyAlert();
      return;
    }

    const db = getFirestore();
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;

    const availCol = getAvailCollectionRef(db, { mode, teamId, uid, availabilityTeamId });
    if (!availCol) return;

    const base = findBase(rec);
    if (!base?.id || !rec?.instanceDate) return;

    const dateKey = rec.instanceDate;
    const nextExceptions = Array.from(new Set([...(base.exceptions || []), dateKey]));

    await updateDoc(doc(availCol, base.id), { exceptions: nextExceptions });

    setEditing(null);
    setPopup(null);
  };

  // ---------- Delete this + all future (truncate) ----------
  const handleDeleteFuture = async (rec) => {
    if (readOnly) {
      showReadOnlyAlert();
      return;
    }

    const db = getFirestore();
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;

    const availCol = getAvailCollectionRef(db, { mode, teamId, uid, availabilityTeamId });
    if (!availCol) return;

    const base = findBase(rec);
    if (!base?.id || !rec?.instanceDate) {
      setEditing(null);
      setPopup(null);
      return;
    }

    const [y, m, d] = rec.instanceDate.split('-').map(Number);
    const cutoff = new Date(y, m - 1, d);
    const back = rec.repeat === 'biweekly' ? 14 : 7;
    cutoff.setDate(cutoff.getDate() - back);
    const newEnd = cutoff.toISOString().slice(0, 10);

    await updateDoc(doc(availCol, base.id), { repeatEndDate: newEnd });

    setEditing(null);
    setPopup(null);
  };

  // ---------- Recurring action menu triggers ----------
  const handleEditInstance = () => {
    if (readOnly) {
      showReadOnlyAlert();
      return;
    }
    setPopup((p) => ({ ...(p || {}), isOverride: true }));
  };
  const handleEditFuture = () => {
    if (readOnly) {
      showReadOnlyAlert();
      return;
    }
    setPopup((p) => ({ ...(p || {}), isSplitFuture: true }));
  };

  // ---------- Top help tooltip positioning ----------
  const positionHelpTooltip = () => {
    if (!helpRef.current || !helpPanelRef.current) return;

    const rootStyle = getComputedStyle(document.documentElement);
    const baseRem = parseFloat(rootStyle.fontSize) || 16;

    // read CSS var (defined in CalendarAvailability.css under .availability-wrapper)
    const padRem =
      parseFloat(rootStyle.getPropertyValue("--CalendarAvailability-help-tip-vp-pad")) || 0.5;
    const padPx = padRem * baseRem;

    const iconRect = helpRef.current.getBoundingClientRect();
    const panelEl = helpPanelRef.current;

    // measure panel (it exists only when open)
    const panelWidth = panelEl.offsetWidth || 22 * baseRem;
    const panelHeight = panelEl.offsetHeight || 0;

    // place it centered under the ? button
    const desiredLeft = iconRect.left + iconRect.width / 2 - panelWidth / 2;
    const desiredTop = iconRect.bottom + 0.4 * baseRem;

    const clampedLeft = Math.max(
      padPx,
      Math.min(desiredLeft, window.innerWidth - panelWidth - padPx)
    );
    const clampedTop = Math.max(
      padPx,
      Math.min(desiredTop, window.innerHeight - panelHeight - padPx)
    );

    panelEl.style.setProperty("--help-tip-left", `${clampedLeft / baseRem}rem`);
    panelEl.style.setProperty("--help-tip-top", `${clampedTop / baseRem}rem`);
  };

  // --- help panel (ReviewDashboard-style) ---
  const [showHelpPanel, setShowHelpPanel] = useState(false);
  const helpPanelRef = useRef(null);


  const [unavailableToggleBusy, setUnavailableToggleBusy] = useState(false);
  const [statusAlert, setStatusAlert] = useState(null);

  const showLoginRequiredAlert = (
    message = "Please Login to use this function"
  ) => {
    setAuthActionAlert(message);
  };

  const showReadOnlyAlert = (message = readOnlyMessage) => {
    setStatusAlert({
      type: "warning",
      message,
    });
    window.setTimeout(() => setStatusAlert(null), 4500);
  };

  // Use ONE ref for the icon/button anchor
  // (you already have helpRef)
  const openHelpPanel = () => {
    setShowHelpPanel(true);

    // wait until the panel is actually in the DOM, then position
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => positionHelpTooltip());
    });
  };

  const closeHelpPanel = () => setShowHelpPanel(false);

  useEffect(() => {
    if (!showHelpPanel) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeHelpPanel();
    };

    const onPointerDown = (e) => {
      const panel = helpPanelRef.current;
      const anchor = helpRef.current;
      if (!panel || !anchor) return;

      if (panel.contains(e.target)) return;
      if (anchor.contains(e.target)) return;

      closeHelpPanel();
    };

    // keep it positioned if the user scrolls/resizes while open
    const onReposition = () => positionHelpTooltip();

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("touchstart", onPointerDown, { passive: true });
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("touchstart", onPointerDown);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [showHelpPanel]);

  // ---------- Derived data for grid ----------
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const hours = Array.from({ length: 24 }, (_, i) => i);

  const displayedWeekKey = useMemo(
    () => getDisplayWeekStartLocal(weekStart, selectedTimeZone).toISODate(),
    [weekStart, selectedTimeZone]
  );

  const weekSubmission = useMemo(() => availabilitySubmission(rawFirestoreList,
    members.map(member => member.uid), displayedWeekKey, mode === 'team'||availabilityTeamId ? teamData.teamTimeZone || 'UTC' : selectedTimeZone),
    [rawFirestoreList, members, displayedWeekKey, teamData.teamTimeZone, mode, selectedTimeZone,availabilityTeamId]);
  const unavailableAllWeekByUid = weekSubmission.unavailable;

  const amUnavailableThisWeek = currentUid
    ? unavailableAllWeekByUid.has(currentUid)
    : false;

  const filteredMembers = useMemo(
    () =>
      members.filter((member) =>
        embedded&&mode==='personal'?member.uid===currentUid:enabledUserIds.has(member.uid)
      ),
    [members, enabledUserIds,embedded,mode,currentUid]
  );

  const filteredAvailByMember = useMemo(() => {
    return Object.fromEntries(filteredMembers.map((m) => [m.uid, availByMember[m.uid] || []]));
  }, [filteredMembers, availByMember]);

  const dayLanes=useMemo(()=>availabilityDayLanes(filteredAvailByMember,filteredMembers),[filteredAvailByMember,filteredMembers]);
  const bookingSegments = useMemo(() => {
    if (mode !== 'team') return [];
    const {weekStartUTCms, weekEndUTCmsExclusive} = getWeekWindowUTC(weekStart, selectedTimeZone);
    return expandRecurring((teamData.events || []).filter(event => event.calendar === 'team'),
      new Date(weekStartUTCms - 86400000), new Date(weekEndUTCmsExclusive))
      .flatMap(event => splitIntervalIntoDisplayWeekSegments(event.startUTC, event.endUTC, weekStart, selectedTimeZone)
        .map(segment => ({...segment, event, uids: bookingParticipantUids(event, members)})));
  }, [mode, teamData.events, members, weekStart, selectedTimeZone]);
  const bookableAvailability = useMemo(() => {
    const otherTeamBookings = Object.entries(busyByUid).flatMap(([uid, intervals]) =>
      intervals.flatMap(([start, end]) => splitIntervalIntoDisplayWeekSegments(
        new Date(start).toISOString(), new Date(end).toISOString(), weekStart, selectedTimeZone
      ).map(segment => ({...segment, uids: [uid]}))));
    return subtractBookings(availByMember, [...bookingSegments, ...otherTeamBookings]);
  }, [availByMember, bookingSegments, busyByUid, weekStart, selectedTimeZone]);

  const openOverlapEvent = (day, overlap) => {
    if (readOnly || !currentUid || mode !== 'team') return;
    const date = getDisplayWeekStartLocal(weekStart, selectedTimeZone).plus({days: day}).toISODate();
    const id = String(Date.now()) + '-' + Math.random().toString(36).slice(2);
    sessionStorage.setItem('availability-event-' + id, JSON.stringify({teamId, date,
      startTime: mmToHHMM(overlap.fromMin), endTime: mmToHHMM(overlap.toMin),
      uids: overlap.uids, timeZone: selectedTimeZone, createdAt: Date.now()}));
    if(onCreateCalendarEvent){
      sessionStorage.removeItem('availability-event-'+id);
      onCreateCalendarEvent({date,startTime:mmToHHMM(overlap.fromMin),endTime:mmToHHMM(overlap.toMin),uids:overlap.uids,teamId});
    }else navigate('/home?view=events&availabilityDraft=' + encodeURIComponent(id));
  };
  eventSelectionRef.current=(day,fromMin,toMin)=>openOverlapEvent(day,{fromMin,toMin,uids:membersAvailableThroughout(bookableAvailability,filteredMembers,day,fromMin,toMin)});
  const showBookingDetails = (event, target) => {
    if(planningOnly){
      setBookingDetails(null);
      const date=target?.closest('[data-availability-date]')?.dataset.availabilityDate||DateTime.fromISO(event.startUTC).setZone(selectedTimeZone).toISODate();
      onPreviewCalendarEvent?.(event,DateTime.fromISO(date,{zone:selectedTimeZone}));
      return;
    }
    keepBookingDetailsOpen();
    const rect = target.getBoundingClientRect();
    setBookingDetails({event, left: Math.max(8, Math.min(rect.left, window.innerWidth - 310)),
      top: Math.max(8, Math.min(rect.bottom + 6, window.innerHeight - 260))});
  };
  const scheduledEvents = day => mode === 'team' && (
    <div className="availability-bookings-summary">
      <strong>Scheduled events</strong>
      {bookingSegments.filter(segment => segment.day === day).map((segment, index) => (
        <button type="button" key={index} className="availability-booking-label" style={{"--booking-color": getEventTagColor(segment.event.tag)}}
          onMouseEnter={e => showBookingDetails(segment.event, e.currentTarget)}
          onMouseLeave={scheduleBookingDetailsClose}
          onBlur={blurBookingDetails}
          onFocus={e => showBookingDetails(segment.event, e.currentTarget)}
          onClick={e => showBookingDetails(segment.event, e.currentTarget)}>
          {mmToHHMM(segment.startMin)}–{mmToHHMM(segment.endMin)} · {segment.event.tag || 'Event'}
        </button>
      ))}
      {!bookingSegments.some(segment => segment.day === day) && <span>None</span>}
    </div>
  );
  const overlapAction = (day, overlap) => mode === 'team' && !readOnly && currentUid && (
    <button type="button" className="availability-overlap-add"
      aria-label={'Create team event ' + mmToHHMM(overlap.fromMin) + '–' + mmToHHMM(overlap.toMin)}
      title="Open Team Calendar with this time and these participants"
      onClick={() => openOverlapEvent(day, overlap)}>+</button>
  );

  const dailySummaries = useMemo(() => {
    const playerUids = members.filter(member => normalizeRoleKey(teamRoles[member.uid] || 'unknown') === 'player').map(member => member.uid);
    // Players/5+ describe the saved availability shown in the grid. Only the
    // explicitly bookable suggestions subtract scheduled events across teams.
    const selected = buildDailySummaries(filteredAvailByMember, filteredMembers);
    const fullRoster = buildDailySummaries(availByMember, members, playerUids);
    const bookable = buildDailySummaries(bookableAvailability, filteredMembers);
    return selected.map((summary, day) => ({...summary, players: fullRoster[day].players,
      overlaps: bookable[day].overlaps}));
  }, [availByMember, filteredAvailByMember, bookableAvailability, filteredMembers, members, teamRoles]);

  const overlapTimes = (overlaps) => overlaps?.length ? overlaps.map((overlap, index) => {
    const participants = ids => ids.map(uid => ({uid,
      name: redactEmail(members.find(member => member.uid === uid)?.name),
      role: normalizeRoleKey(teamRoles[uid] || 'unknown')}));
    return <React.Fragment key={index}>
      {index > 0 && ', '}
      <AvailabilityOverlapTime className="availability-overlap-time" participants={participants(overlap.uids)} heading="Available members">
        {mmToHHMM(overlap.fromMin)}–{mmToHHMM(overlap.toMin)}
      </AvailabilityOverlapTime>
      {overlap.extraUids?.length > 0 && <AvailabilityOverlapTime className="availability-overlap-extra"
        participants={participants(overlap.extraUids)} heading="Also available for this entire time"> +{overlap.extraUids.length}</AvailabilityOverlapTime>}
    </React.Fragment>;
  }) : '—';

  // ✅ Send "request availability" + show sender confirmation
  const handleRequestAvailabilityClick = async (targetUid) => {
    if (mode !== "team") return;
    if (!teamId) return;
    if (!targetUid) return;

    if (typeof onRequestMemberAvailability !== "function") {
      setRequestAlert({
        type: "error",
        message: "Request function is not connected (onRequestMemberAvailability missing).",
      });
      return;
    }

    try {
      setRequestSendingUid(targetUid);

      const res = await onRequestMemberAvailability({
        teamId,
        targetUid,
        weekStart, // pass Date; page-level helper will convert to weekKey
      });

      // res can be either the raw callable data OR a wrapped object depending on your implementation
      const data = res?.data || res || {};

      if (data?.ok && data?.skipped) {
        setRequestAlert({
          type: "info",
          message: "Already requested recently — skipped to avoid spam.",
        });
      } else if (data?.ok && data?.skippedByPrefs) {
        setRequestAlert({
          type: "info",
          message: "Not sent (the member’s notification preferences block this request).",
        });
      } else if (data?.ok) {
        const inApp = data?.inAppSent ? "In-app" : null;
        const discord = data?.discordSent ? "Discord" : null;
        const via = [inApp, discord].filter(Boolean).join(" + ");
        setRequestAlert({
          type: "success",
          message: `Request sent${via ? ` (${via})` : ""}.`,
        });
      } else {
        setRequestAlert({
          type: "error",
          message: "Request failed — please try again.",
        });
      }
    } catch (e) {
      const msg =
        e?.message ||
        e?.toString?.() ||
        "Request failed — please try again.";
      setRequestAlert({ type: "error", message: msg });
    } finally {
      setRequestSendingUid(null);

      // auto-hide after a few seconds
      window.setTimeout(() => setRequestAlert(null), 4500);
    }
  };

  useLayoutEffect(()=>{
    if(!addButtonRef)return undefined;
    addButtonRef.current=requestedDay=>{
      if(readOnly||editing)return;
      const value=displayDate||weekStart,day=requestedDay||DateTime.fromObject({year:value.getFullYear(),month:value.getMonth()+1,day:value.getDate()},{zone:selectedTimeZone});
      handleSelectEntireDay(day.weekday-1,null,day);
      const startDate=new Date(day.year,day.month-1,day.day,18),endDate=new Date(day.year,day.month-1,day.day,19);
      setPopup(previous=>({...previous,startDate,endDate}));
      setSelectionRange({day:day.weekday-1,from:1080,to:1140});
      setPopupPos({x:8,y:8});
    };
    return()=>{addButtonRef.current=null;};
  });

  const floatingPanel=content=>embedded&&content?createPortal(content,document.body):content;
  const floatingTop=Math.max(8,Math.min((containerRef.current?.getBoundingClientRect().top||0)+popupPos.y,window.innerHeight-520));
  const floatingLeft=Math.max(8,Math.min((containerRef.current?.getBoundingClientRect().left||0)+popupPos.x,window.innerWidth-308));
  const floatingStyle=embedded?{position:'fixed',zIndex:22010,left:floatingLeft,top:floatingTop,'--availability-popup-top':`${floatingTop}px`,'--availability-popup-left':`${floatingLeft}px`,maxHeight:Math.max(100,window.innerHeight-floatingTop-8),overflow:'auto',maxWidth:'calc(100vw - 16px)'}:{top:popupPos.y,left:popupPos.x};
  const hasAvailabilityActions=mode==='personal'&&availabilityTeamId?!readOnly&&!!onToggleUnavailableThisWeek:mode==='team'?!readOnly&&(typeof onImportFromPersonal==='function'||typeof onToggleUnavailableThisWeek==='function'):!!onImportAllTeams;
  const weekDays=getDisplayWeekStartLocal(weekStart,selectedTimeZone);
  const todayIndex=Array.from({length:7},(_,index)=>weekDays.plus({days:index}).toISODate()).indexOf(DateTime.now().setZone(selectedTimeZone).toISODate());
  // ---------- Render ----------
  return (
    <div className={`availability-wrapper${embedded?' availability-wrapper--embedded':''}${planningOnly?' availability-wrapper--planning':''}`} ref={containerRef}>
      {requestAlert && (
        <div className="CalendarAvailability-AlertOverlay" role="status" aria-live="polite">
          <CustomAlert
            type={requestAlert.type}
            message={requestAlert.message}
            onClose={() => setRequestAlert(null)}
            position="top"
          />
        </div>
      )}

      {statusAlert && (
        <div className="CalendarAvailability-AlertOverlay" role="status" aria-live="polite">
          <CustomAlert
            type={statusAlert.type}
            message={statusAlert.message}
            onClose={() => setStatusAlert(null)}
            position="top"
          />
        </div>
      )}

      {authActionAlert && (
        <div className="CalendarAvailability-AlertOverlay" role="status" aria-live="assertive">
          <CustomAlert
            type="auth"
            message={authActionAlert}
            position="top"
            autoCloseMs={3200}
            onClose={() => setAuthActionAlert("")}
            actions={[
              {
                label: "Login",
                onClick: () => {
                  setAuthActionAlert("");
                  navigate("/login");
                },
              },
            ]}
          />
        </div>
      )}

      {/* top nav */}
      <AvailabilityToolsPortal target={toolsPortal}>      <div className="calendar-availability-nav">
        {!embedded&&<div className="nav-left">
          <button onClick={onPrevWeek}>←</button>
          <button onClick={onToday} className="CalendaerAvailability-Today-btn">
            Today
          </button>
          <button onClick={onNextWeek}>→</button>
        </div>}

        {!embedded&&<div className="nav-center">
          <select value={selectedTimeZone} onChange={(e) => onTimeZoneChange(e.target.value)}>
            {getTimeZoneOptions(selectedTimeZone).map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </div>}

        <div className={`nav-right${embedded?' availability-toolbar':''}`} role={embedded?'group':undefined} aria-label={embedded?'Availability controls':undefined}>
          <div className={embedded?'availability-toolbar__group availability-toolbar__display':'availability-nav-group'} role={embedded?'group':undefined} aria-label={embedded?'Display options':undefined}>
          {embedded&&!planningOnly&&<span className="availability-toolbar__label">Display</span>}
          <div className={embedded?'availability-toolbar__items':'availability-nav-group'}>
          {mode === 'team' && <label className="availability-events-toggle">
            <input type="checkbox" checked={showTeamEvents} onChange={e => {setShowTeamEvents(e.target.checked); setBookingDetails(null);}} />{embedded&&<CalendarDays size={14} aria-hidden="true"/>} Show team events
          </label>}
          {embedded&&mode==='personal'?<label className="overview-personal-color"><span>{redactEmail(members.find(member=>member.uid===currentUid)?.name||'You')}</span><input type="color" aria-label="Pick my availability color" value={userColors[currentUid]||getHexColorForUid(currentUid)} onChange={event=>handleSaveMyColor(event.target.value)}/></label>:!inlineMembers&&<>          <button type="button" ref={membersButtonRef} aria-expanded={showRoleMenu} aria-controls={showRoleMenu?membersMenuId:undefined} aria-haspopup="dialog" onClick={() => setShowRoleMenu(v=>!v)} className="Calendar-Availability-Roles-btn">
            {embedded?(mode==='team'?<><Users size={14} aria-hidden="true"/>Members<span className="availability-toolbar__count">{filteredMembers.length}/{members.length}</span><ChevronDown size={12} aria-hidden="true"/></>:<><Palette size={14} aria-hidden="true"/>Color & visibility<ChevronDown size={12} aria-hidden="true"/></>):'Members…'}
          </button></>}
          {!planningOnly&&isMobile && (!embedded||(mode==='team'&&display==='detailed')) && (
            <button
              type="button"
              className="CalendarAvailability-SummaryToggle"
              aria-expanded={showMobileSummary}
              onClick={() => setShowMobileSummary((v) => !v)}
            >
              {embedded&&<Rows3 size={14} aria-hidden="true"/>}
              {showMobileSummary ? "Hide Summary" : "Show Summary"}
            </button>
          )}
          </div></div>
          {(!embedded||hasAvailabilityActions)&&<div className={embedded?'availability-toolbar__group availability-toolbar__actions':'availability-nav-group'} role={embedded?'group':undefined} aria-label={embedded?(mode==='team'?'My availability actions':'Share personal availability'):undefined}>
          {embedded&&<span className="availability-toolbar__label">{mode==='team'?'My availability':availabilityTeamId?'This team’s week':'Share availability'}</span>}
          <div className={embedded?'availability-toolbar__items':'availability-nav-group'}>
          {mode === 'team' && typeof onImportFromPersonal === 'function' && !readOnly && (
            <button
              type="button"
              className="Availability-Import-Button"
              onClick={() => onImportFromPersonal(weekStart)}
              title="Import all of this week's personal availability into this team (editable after import)"
            >
              {embedded?<><ArrowDownToLine size={14} aria-hidden="true"/>Import from Personal</>:'Import this week from Personal'}
            </button>
          )}

          {(mode === "team" || !!availabilityTeamId) && typeof onToggleUnavailableThisWeek === "function" && !readOnly && (
            <button
              type="button"
              className={`Availability-Unavailable-Button ${amUnavailableThisWeek ? "is-active" : ""}`}
              onClick={async () => {
                try {
                  setUnavailableToggleBusy(true);
                  await onToggleUnavailableThisWeek({
                    weekStart,
                    unavailable: !amUnavailableThisWeek,
                  });

                  setStatusAlert({
                    type: "success",
                    message: !amUnavailableThisWeek
                      ? "You are marked as unavailable for this week."
                      : "Your unavailable-for-this-week status was removed.",
                  });
                } catch (e) {
                  setStatusAlert({
                    type: "error",
                    message: e?.message || "Could not update unavailable status.",
                  });
                } finally {
                  setUnavailableToggleBusy(false);
                  window.setTimeout(() => setStatusAlert(null), 4500);
                }
              }}
              disabled={unavailableToggleBusy}
              aria-pressed={amUnavailableThisWeek}
              aria-busy={unavailableToggleBusy}
              title={
                amUnavailableThisWeek
                  ? "Remove unavailable-all-week status"
                  : "Mark yourself unavailable for the entire displayed week"
              }
            >
              {embedded?(<>{unavailableToggleBusy?<LoaderCircle size={14} className="availability-toolbar__spinner" aria-hidden="true"/>:amUnavailableThisWeek?<Undo2 size={14} aria-hidden="true"/>:<CalendarOff size={14} aria-hidden="true"/>}{unavailableToggleBusy?'Updating…':amUnavailableThisWeek?'Undo unavailable week':'Unavailable this week'}</>):unavailableToggleBusy
                ? "…"
                : amUnavailableThisWeek
                ? "avail. after all"
                : "unavail. this week"}
            </button>
          )}
          {mode==='personal'&&onImportAllTeams&&<button type="button" className="Availability-Import-Button" onClick={onImportAllTeams}>{embedded&&<Copy size={14} aria-hidden="true"/>}Import to all teams</button>}
          </div></div>}

          {!embedded&&(mode === 'team' ? (
            <button
              className="Availability-Switch-Button availability-my"
              onClick={() => onSwitchMode?onSwitchMode('personal'):navigate('/home?view=personal')}
              title="Open Personal Availability"
            >
              Personal Availability
            </button>
          ) : (
            <button
              className="Availability-Switch-Button availability-team"
              onClick={() => onSwitchMode?onSwitchMode('team'):navigate('/manager?view=availability')}
              title="Go to Team Availability"
            >
              Team Availability
            </button>
          ))}

          {!embedded&&<button onClick={onBackToCalendar} className="Back-To-Calendar">
            ← Back to Calendar
          </button>}

          {/* help button (click-to-open panel) */}
{showHelp&&<AvailabilityToolsPortal target={helpPortal}>          <div className="availability-helpWrap">
            <button
              type="button"
              className="availability-helpBtn"
              ref={helpRef}
              aria-haspopup="dialog"
              aria-expanded={showHelpPanel}
              aria-label={mode==='personal'?'Help: Personal Availability':'Help: Team Availability'}
              onClick={() => {
                if (showHelpPanel) closeHelpPanel();
                else openHelpPanel();
              }}
              onMouseEnter={() => {
                // optional: pre-position so it doesn't "jump" on click
                positionHelpTooltip();
              }}
              onFocus={() => {
                positionHelpTooltip();
              }}
            >
              {embedded?<><CircleHelp size={16} aria-hidden="true"/><span>Help</span></>:'?'}
            </button>

            {floatingPanel(showHelpPanel && (
              <div
                className="availability-helpPanel"
                ref={helpPanelRef}
                role="dialog"
                aria-label={mode==='personal'?'Personal Availability help':'Team Availability help'}
                // positionHelpTooltip writes these CSS vars:
                // --help-tip-left and --help-tip-top
              >
                {/* Header row (prevents X from overlapping the first dropdown arrow) */}
                <div className="availability-helpHeader">
                  <div className="availability-helpHeaderTitle">{mode==='personal'?'Personal':'Team'} Availability — Help</div>

                  <button
                    type="button"
                    className="availability-helpPanelClose app-cancel-control"
                    onClick={closeHelpPanel}
                    aria-label="Close help"
                  >
                    ×
                  </button>
                </div>

                {/* Scroll container (this is what makes the tooltip scrollable) */}
                <div className="availability-helpPanelBody">
                  {/* =========================
                      HELP TOPICS (user-focused)
                    ========================= */}

                  <details className="availability-helpAcc" open>
                    <summary className="availability-helpAccSum">What this page is for</summary>
                    <div className="availability-helpAccBody">
                      <p className="availability-helpP">
                        This is your team’s <strong>Availability Planner</strong>. It lets everyone add when they’re free,
                        then automatically highlights <strong>the best overlap windows</strong> so you can schedule scrims,
                        VOD reviews, meetings, or ranked sessions without endless back-and-forth.
                      </p>
                      <ul className="availability-helpList">
                        <li><strong>Add availability</strong> by clicking and dragging on the grid (15-minute precision).</li>
                        <li><strong>Edit</strong> your blocks by clicking your colored overlay.</li>
                        <li><strong>See team overlap</strong> at the top (All / 5+ / overlaps ≥ 1 hour).</li>
                        <li><strong>Filter who counts</strong> using the <strong>Members…</strong> menu.</li>
                      </ul>
                    </div>
                  </details>

                  <details className="availability-helpAcc">
                    <summary className="availability-helpAccSum">How to add, edit, and delete availability</summary>
                    <div className="availability-helpAccBody">
                      <ul className="availability-helpList">
                        <li>
                          <strong>Create:</strong> click + drag on any day/hour cell to select a time range, then press <strong>Confirm</strong>.
                        </li>
                        <li>
                          <strong>Edit:</strong> click your colored block to open the editor and press <strong>Save Changes</strong>.
                        </li>
                        <li>
                          <strong>Delete:</strong> open your block and press <strong>Delete</strong> (removes that availability entry).
                        </li>
                        <li>
                          <strong>Time granularity:</strong> everything snaps to <strong>15-minute</strong> increments.
                        </li>
                      </ul>
                    </div>
                  </details>

                  <details className="availability-helpAcc">
                    <summary className="availability-helpAccSum">Repeating availability (weekly / biweekly)</summary>
                    <div className="availability-helpAccBody">
                      <p className="availability-helpP">
                        If your schedule repeats, set <strong>Repeat</strong> to <strong>Every week</strong> or <strong>Every 2 weeks</strong>,
                        then choose an <strong>Until</strong> date. This keeps your schedule consistent without re-entering it every week.
                      </p>
                      <ul className="availability-helpList">
                        <li><strong>Edit this instance</strong> = change only the selected week (great for a one-off exception).</li>
                        <li><strong>Edit all future</strong> = update the schedule going forward.</li>
                        <li><strong>Delete this instance</strong> = remove only that week.</li>
                        <li><strong>Delete all future</strong> = stop the recurring series going forward.</li>
                      </ul>
                    </div>
                  </details>

                  <details className="availability-helpAcc">
                    <summary className="availability-helpAccSum">Time zones (why times might look different)</summary>
                    <div className="availability-helpAccBody">
                      <p className="availability-helpP">
                        The time zone selector changes how times are <strong>displayed</strong> so teammates in different regions
                        can view the same availability in their local time. If something looks “shifted”, double-check the selected time zone.
                      </p>
                    </div>
                  </details>

                  <details className="availability-helpAcc">
                    <summary className="availability-helpAccSum">Import this week from Personal</summary>
                    <div className="availability-helpAccBody">
                      <p className="availability-helpP">
                        If you already maintain your schedule in <strong>My Availability</strong>, use
                        <strong> Import this week from Personal</strong> to copy those blocks into the team week.
                        After importing, you can still edit your team availability normally.
                      </p>
                    </div>
                  </details>

                  <details className="availability-helpAcc">
                    <summary className="availability-helpAccSum">Members… menu (filtering + what each item means)</summary>
                    <div className="availability-helpAccBody">
                      <p className="availability-helpP">
                        The <strong>Members…</strong> menu decides who is included in the grid and overlap calculations.
                        If someone is unchecked, their availability is hidden and they will not count toward <strong>5+</strong> or bookable overlap windows. Players always checks the full Player roster.
                      </p>

                      <ul className="availability-helpList">
                        <li>
                          <strong>✓ / — indicator</strong> (left of each name):
                          <ul className="availability-helpList availability-helpListInner">
                            <li><strong>✓</strong> = this member has availability recorded for the team’s week, including saved recurring availability.</li>
                            <li><strong>—</strong> = no availability recorded for the team’s week (not “unavailable”, just “not submitted”).</li>
                          </ul>
                        </li>

                        <li><strong>Checkbox</strong> = include/exclude that person from the view and from overlap math.</li>

                        <li>
                          <strong>Role-colored username</strong> = a quick visual tag for their team role (explained in the next topic).
                        </li>

                        <li>
                          <strong><NotificationIcon size={14}/> Request availability</strong> = sends a friendly nudge asking them to add availability for this week.
                          (You’ll see a confirmation banner if it sent successfully.)
                        </li>

                        <li>
                          <strong>My color picker</strong> (only on your own row) = choose the color used for your availability blocks.
                          This only changes the visual color, not the times.
                        </li>

                        <li>
                          <strong>Select all / Deselect all / Just me</strong> = quick presets to manage the filter fast.
                        </li>
                      </ul>
                    </div>
                  </details>

                  <details className="availability-helpAcc">
                    <summary className="availability-helpAccSum">Role colors (why usernames change color)</summary>
                    <div className="availability-helpAccBody">
                      <p className="availability-helpP">
                        Names are color-coded by <strong>team role</strong> so you can instantly see who’s available:
                        Players, Coaches, Analysts, Subs, Managers, Spectators, and Trials.
                      </p>

                      <ul className="availability-helpList">
                        <li>Role colors appear in the <strong>Members…</strong> list and in the <strong>Team overlaps (≥1h)</strong> list.</li>
                        <li>Availability blocks in the grid use each user’s <strong>availability color</strong> (your chosen color), not the role color.</li>
                      </ul>
                    </div>
                  </details>

                  <details className="availability-helpAcc">
                    <summary className="availability-helpAccSum">Team Overlap: Players, 5+, and overlaps</summary>
                    <div className="availability-helpAccBody">
                      <p className="availability-helpP">
                        The overlap summary is built for scheduling. It only counts windows that are at least <strong>1 hour long</strong>
                        (continuous) so the results are actually usable.
                      </p>

                      <ul className="availability-helpList">
                        <li><strong>Players</strong> = all rostered Players have saved availability for at least one continuous hour, including time already scheduled on their calendars. The +number lists other team members available for that entire window. Hover a time to see its participants.</li>
                        <li>
                          <strong>5+</strong> = at least <strong>5 enabled</strong> members are available for the entire window.
                          Includes time already scheduled on their calendars.
                          Best for VOD review, ranked stacks, or flexible practice blocks.
                        </li>
                        <li>
                          <strong>Team overlaps (≥1h)</strong> = every window where <strong>2+</strong> enabled members overlap for ≥ 1 hour,
                          excluding known scheduled events on this and other teams, with exact participant names (role-colored).
                        </li>
                      </ul>

                      <p className="availability-helpP">
                        Filtering in <strong>Members…</strong> updates 5+ and bookable overlaps. Players always includes the full Player roster, even if a Player is hidden in the grid.
                      </p>
                    </div>
                  </details>

                  <details className="availability-helpAcc">
                    <summary className="availability-helpAccSum">Grid colors (green / blue) and what they mean</summary>
                    <div className="availability-helpAccBody">
                      <ul className="availability-helpList">
                        <li><strong>Green hour</strong> = “All” enabled members are available for that full hour.</li>
                        <li><strong>Blue hour</strong> = at least 5 enabled members are available for that full hour.</li>
                        <li>
                          The colored vertical blocks inside each hour are the actual individual availabilities.
                          Multiple people in the same hour show side-by-side.
                        </li>
                      </ul>
                    </div>
                  </details>

                  <details className="availability-helpAcc">
                    <summary className="availability-helpAccSum">Mobile view tips</summary>
                    <div className="availability-helpAccBody">
                      <ul className="availability-helpList">
                        <li><strong>Team Overlap</strong> becomes a horizontal strip so it stays readable on small screens.</li>
                        <li>Use <strong>Show Summary / Hide Summary</strong> if you want more grid space.</li>
                        <li>Menus and the help panel scroll if they don’t fit on screen.</li>
                      </ul>
                    </div>
                  </details>
                </div>
              </div>
            ))}

          </div></AvailabilityToolsPortal>}
        </div>

        {(inlineMembers?content=>createPortal(content,toolsPortal):floatingPanel)((showRoleMenu||inlineMembers) && (
          <div
            className={`role-filter-menu${inlineMembers?' availability-side-members':''}`}
            id={membersMenuId}
            style={embedded&&!inlineMembers?{position:'fixed',zIndex:22000,right:'auto',...membersMenuPosition,visibility:membersMenuPosition?'visible':'hidden',maxHeight:'min(70dvh, calc(100dvh - 16px))'}:undefined}
            role={inlineMembers?"group":"dialog"}
            aria-label={inlineMembers?"Team members":"Member filter"}
            ref={membersMenuRef}
          >
            {!inlineMembers&&<button
              className="role-filter-close app-cancel-control"
              onClick={() => setShowRoleMenu(false)}
              aria-label="Close member filter"
            >
              ×
            </button>}

            <div className={inlineMembers?'availability-member-header':undefined}><div className="role-filter-title">{inlineMembers?`Members · ${filteredMembers.length}/${members.length}`:embedded&&mode==='personal'?'Color & visibility':'Show availability for:'}</div>
            {inlineMembers&&<div className="availability-member-groups" role="group" aria-label="Select member groups">{[
              {label:members.length>0&&members.every(member=>enabledUserIds.has(member.uid))?'Deselect All':'Select All',ids:members.map(member=>member.uid),all:true},
              {label:'Me',ids:members.some(member=>member.uid===currentUid)?[currentUid]:[]},
              ...[['Players','player'],['Subs','sub'],['Coach','coach']].map(([label,role])=>({label,ids:members.filter(member=>normalizeRoleKey(teamRoles[member.uid])===role).map(member=>member.uid)})),
            ].map(group=>{const selected=group.ids.filter(id=>enabledUserIds.has(id)).length;return <button type="button" key={group.all?'all':group.label} disabled={!group.ids.length} aria-pressed={selected===0?false:selected===group.ids.length?true:'mixed'} onClick={()=>toggleMemberGroup(group.ids)}>{group.label}</button>;})}</div>}</div>

            <ul className="role-filter-list">
              {[...members]
                .sort((a, b) => {
                  const ra = ROLE_ORDER.indexOf(normalizeRoleKey(teamRoles?.[a.uid] || 'player'));
                  const rb = ROLE_ORDER.indexOf(normalizeRoleKey(teamRoles?.[b.uid] || 'player'));
                  if (ra !== rb) return ra - rb;
                  return (a.name || '').localeCompare(b.name || '', undefined, {
                    sensitivity: 'base',
                  });
                })
                .map((m) => {
                  const uid = m.uid;
                  const checked = enabledUserIds.has(uid);
                  const roleKey = normalizeRoleKey(teamRoles?.[uid] || "player");

                  // Submission belongs to the TEAM week, independent of display zone.
                  const isUnavailableThisWeek = unavailableAllWeekByUid.has(uid);
                  const hasThisWeek = weekSubmission.submitted.has(uid);

                  return (
                    <li key={uid}>
                      <label className={`role-filter-item role-${roleKey} CalAvail-memberRow`}>
                        {/* ✅ week status FIRST (left of checkbox) */}
                        <span
                        className={`member-week-status ${
                          isUnavailableThisWeek ? "unavailable" : hasThisWeek ? "ok" : "missing"
                        }`}
                        title={
                          isUnavailableThisWeek
                            ? "Marked unavailable all week"
                            : hasThisWeek
                            ? "Availabilities added for this week"
                            : "No availabilities added for this week"
                        }
                        aria-label={
                          isUnavailableThisWeek
                            ? "Marked unavailable all week"
                            : hasThisWeek
                            ? "Has availabilities this week"
                            : "Missing availabilities this week"
                        }
                      >
                        {isUnavailableThisWeek ? "∅" : hasThisWeek ? "✓" : "—"}
                      </span>

                        {/* ✅ checkbox SECOND */}
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleUser(uid)}
                        />

                        {/* ✅ name THIRD */}
                        <span className="member-name">{redactEmail(m.name)}</span>{inlineMembers&&<><span className="availability-member-role" style={{color:teamRoleColor(roleKey)}}>{teamRoleLabel(roleKey)}</span><i className="availability-member-color" style={{background:userColors[uid]||getHexColorForUid(uid)}} title="Availability color"/></>}

                        {mode === "team" && typeof onRequestMemberAvailability === "function" && uid !== currentUid && (
                          <button
                            type="button"
                            className="CalAvail-memberNotifyBtn"
                            title={requestSendingUid === uid ? "Sending..." : "Request availability for this week"}
                            aria-label={`Request availability from ${redactEmail(m.name)}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleRequestAvailabilityClick(uid);
                            }}
                            disabled={requestSendingUid === uid}
                          >
                            {requestSendingUid === uid ? <LoaderCircle size={14} className="is-spinning"/> : <NotificationIcon size={14}/>}
                          </button>
                        )}

                        {currentUid === uid && (
                          <span className="availability-color-picker">
                            <input
                              type="color"
                              value={userColors[uid] || getHexColorForUid(uid)}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => handleSaveMyColor(e.target.value)}
                              aria-label="Pick my availability color"
                              className="availability-color-input"
                            />
                          </span>
                        )}
                      </label>
                    </li>
                  );

                })}
            </ul>

            {!inlineMembers&&<div className="role-filter-actions">
              <button onClick={() => setEnabledUserIds(new Set(members.map((m) => m.uid)))}>
                Select all
              </button>
              <button onClick={() => setEnabledUserIds(new Set())}>Deselect all</button>
              {currentUid && (
                <button onClick={() => setEnabledUserIds(new Set([currentUid]))}>Just me</button>
              )}
            </div>}
          </div>
        ))}
      </div></AvailabilityToolsPortal>

      {/* mobile team overlap summary */}
      {!planningOnly&&display==='detailed' && isMobile && showMobileSummary && (!embedded||mode==='team') && (
        <div className="availability-summary-mobile" aria-label="Team overlap summary">
          <div className="availability-summary-mobile__title">Team Overlap</div>

          <div className="availability-summary-mobile__days" role="list">
            {days.map((d, i) => {
              const s = dailySummaries[i] || { players: [], fivePlus: [] };

              return (
                <div className="availability-summary-mobile__card" role="listitem" key={d}>
                  <div className="availability-summary-mobile__day">
                    <span className="availability-summary-mobile__dow">{d}</span>
                    <span className="availability-summary-mobile__date">{getDayLabel(i)}</span>
                  </div>

                  <div className="availability-summary-mobile__row">
                    <span className="availability-summary-mobile__chip availability-summary-mobile__chip--all">Players:</span>
                    <span className="availability-summary-mobile__times">{overlapTimes(s.players)}</span>
                  </div>

                  <div className="availability-summary-mobile__row">
                    <span className="availability-summary-mobile__chip availability-summary-mobile__chip--five">5+</span>
                    <span className="availability-summary-mobile__times">{overlapTimes(s.fivePlus)}</span>
                  </div>
                  {scheduledEvents(i)}
                  <strong>Bookable overlaps (≥1h)</strong>
                  {(s.overlaps || []).map((overlap, index) => <div key={index} className="summary-overlaps-item">
                    {mmToHHMM(overlap.fromMin)}–{mmToHHMM(overlap.toMin)} · {overlap.uids.length} members
                    {overlapAction(i, overlap)}
                  </div>)}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {display!=='detailed'&&<AvailabilityDisplayViews overlapList={embedded} planningOnly={planningOnly} showBookings={showTeamEvents} renderScheduledEvent={renderScheduledEvent} display={display} date={displayDate||new Date(weekStart)} zone={selectedTimeZone} records={rawFirestoreList} members={members} selectedMembers={filteredMembers} roles={teamRoles} colors={userColors} currentUid={currentUid} mode={mode} teamZone={mode==='team'||availabilityTeamId?teamData.teamTimeZone||'UTC':selectedTimeZone} events={mode==='team'?(teamData.events||[]).map(event=>({...event,calendar:'team',__teamId:teamId})):[]} busyByUid={busyByUid} readOnly={readOnly}
        renderOverlap={overlapTimes} onEdit={(record,day,click)=>handleEditAvailabilityBySource({sourceId:record.id,instanceDate:record.instanceDate,record},day.weekday-1,day.hour,click)} onAddDate={(day,click)=>embedded&&mode==='personal'&&addButtonRef?.current?addButtonRef.current(day):handleSelectEntireDay(day.weekday-1,click,day)} onBooking={showBookingDetails} onCreateOverlap={(day,overlap)=>{
          if(onCreateCalendarEvent)onCreateCalendarEvent({date:day.toISODate(),startTime:mmToHHMM(overlap.fromMin),endTime:mmToHHMM(overlap.toMin),uids:overlap.uids,teamId});
          else openOverlapEvent(day.weekday-1,overlap);
        }}/>}
      {/* grid */}
      {display==='detailed'&&<div
        className={`availability-container ${
          isMobile && !showMobileSummary ? "is-mobile-summary-collapsed" : ""
        }`}
      >
        {!planningOnly&&!isMobile && (!embedded||mode==='team') && (
          <>
            {/* summary row */}
            <div className="avail-cell header summary-corner">Team Overlap</div>
            {days.map((d, i) => {
              const s = dailySummaries[i] || { players: [], fivePlus: [], overlaps: [] };
              return (
                <div key={`summary-${d}`} className="avail-cell summary-cell" data-today={i===todayIndex||undefined}>
                  <div className="summary-line">
                    <div className="summary-chip all">
                      Players:&nbsp;
                      <span className="summary-times">
                        {overlapTimes(s.players)}
                      </span>
                    </div>
                  </div>

                  <div className="summary-line">
                    <div className="summary-chip five">
                      5+:&nbsp;
                      <span className="summary-times">
                        {overlapTimes(s.fivePlus)}
                      </span>
                    </div>
                  </div>

                  {scheduledEvents(i)}
                  <div className="summary-overlaps">
                    <div className="summary-overlaps-title">Bookable overlaps (≥1h):</div>
                    {s.overlaps.length ? (
                      <ul className="summary-overlaps-list">
                        {s.overlaps.map(({ fromMin, toMin, uids }, idx) => (
                          <li key={idx} className="summary-overlaps-item">
                            {overlapAction(i, {fromMin, toMin, uids})}
                            <span className="summary-interval">
                              {mmToHHMM(fromMin)}–{mmToHHMM(toMin)}
                            </span>
                            <span className="summary-names">
                              {' — '}
                              {uids.map((uid, ii) => {
                                const m = filteredMembers.find((x) => x.uid === uid);
                                const name = redactEmail(m?.name);
                                const roleRaw = normalizeRoleKey(teamRoles?.[uid] || 'player');
                                return (
                                  <React.Fragment key={uid}>
                                    {ii > 0 && ', '}
                                    <span className={`role-name role-${roleRaw}`}>{name}</span>
                                  </React.Fragment>
                                );
                              })}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="summary-none">—</div>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}
        
        {/* top-left corner */}
        <div className="avail-cell header availability-time-corner" />

        {/* day headers */}
        {days.map((d, i) => {
          const label=getDisplayWeekStartLocal(weekStart,selectedTimeZone).plus({days:i}).toFormat('LLL d');

          return (
            <div
              key={d}
              className="avail-cell header avail-cell-header-clickable"
              data-today={i===todayIndex||undefined}
              role="button"
              tabIndex={editing ? -1 : 0}
              title={`Select entire ${d} (${label})`}
              aria-label={`Select entire ${d} ${label}`}
              onClick={planningOnly?undefined:(e) => handleSelectEntireDay(i, e)}
              onKeyDown={(e) => {
                if (editing) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSelectEntireDay(i, e);
                }
              }}
            >
              {embedded?<CalendarDayHeading day={weekDays.plus({days:i})} count={mode==='team'?dailySummaries[i]?.overlaps.length||0:Object.values(filteredAvailByMember).flat().filter(record=>record.day===i).length} itemLabel={mode==='team'?'overlap':'block'} today={i===todayIndex}/>:<>{d} <span className="header-date">{label}</span></>}
            </div>
          );
        })}

        {/* hour rows */}
        {hours.map((hour) => (
          <React.Fragment key={hour}>
            <div className="avail-cell hour-label">{`${hour}:00`}</div>

            {days.map((_, day) => {
              const key = `${day}-${hour}`;

              // whole-hour "common/five" background logic (same as you had)
              const fullHourCount = filteredMembers.reduce((sum, m) => {
                const evts = filteredAvailByMember[m.uid] || [];
                return (
                  sum +
                  (evts.some(
                    (evt) =>
                      evt.day === day &&
                      evt.startMin <= hour * 60 &&
                      evt.endMin >= (hour + 1) * 60
                  )
                    ? 1
                    : 0)
                );
              }, 0);

              let cls = '';
              if (fullHourCount > 0) {
                if (fullHourCount === filteredMembers.length) cls = 'common';
                else if (fullHourCount >= 5) cls = 'five';
              }

              const cellStart = hour * 60;
              const cellEnd = cellStart + 60;

              const eventsThisCell = filteredMembers
                .flatMap((m) =>
                  (filteredAvailByMember[m.uid] || []).map((evt) => {
                    if (evt.day !== day) return null;
                    const from = Math.max(evt.startMin, cellStart);
                    const to = Math.min(evt.endMin, cellEnd);
                    if (to <= from) return null;
                    return { m, evt, from, to };
                  })
                )
                .filter(Boolean);

              // dedupe within cell
              const dedupedEventsThisCell = [];
              const seen = new Set();
              for (const entry of eventsThisCell) {
                const k = `${entry.m.uid}|${entry.evt.day}|${entry.from}|${entry.to}`;
                if (seen.has(k)) continue;
                seen.add(k);
                dedupedEventsThisCell.push(entry);
              }

              dedupedEventsThisCell.sort((a, b) => a.m.uid.localeCompare(b.m.uid));

              const userBlocks = dedupedEventsThisCell.map((entry) => {
                const { m, evt, from, to } = entry;
                const topPct = ((from - cellStart) / 60) * 100;
                const heightPct = ((to - from) / 60) * 100;
                const widthPct = 100 / Math.max(1,dayLanes[day].length);
                const leftPct = dayLanes[day].indexOf(m.uid) * widthPct;
                const role=normalizeRoleKey(teamRoles[m.uid]||'unknown');

                return (
                  <div
                    key={`${m.uid}-${evt.day}-${from}-${to}`}
                    data-member-uid={m.uid}
                    data-lane={dayLanes[day].indexOf(m.uid)}
                    title={`${redactEmail(m.name)} · ${teamRoleLabel(role)}`}
                    className={`user-overlay ${!planningOnly&&m.uid === currentUid ? 'editable' : ''}`}
                    role={!planningOnly&&m.uid === currentUid ? 'button' : undefined}
                    tabIndex={!planningOnly&&m.uid === currentUid ? 0 : -1}
                    onKeyDown={(e) => {
                      if (planningOnly||m.uid !== currentUid) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleEditAvailabilityBySource(evt, day, hour, e);
                        e.stopPropagation();
                        e.preventDefault();
                      }
                    }}
                    style={{
                      top: `${topPct}%`,
                      height: `${heightPct}%`,
                      backgroundColor: userColors[m.uid] || getHexColorForUid(m.uid),
                      "--availability-role-color":teamRoleColor(role),
                      width: `${widthPct}%`,
                      left: `${leftPct}%`,
                    }}
                    onClick={(e) => {
                      if (planningOnly||m.uid !== currentUid) return;
                      e.stopPropagation();
                      e.preventDefault();
                      handleEditAvailabilityBySource(evt, day, hour, e); // 🔥 evt now carries sourceId
                    }}

                    onMouseEnter={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setHoveredUser({
                        name: redactEmail(m.name),role:teamRoleLabel(role),roleColor:teamRoleColor(role),
                        x: e.clientX,
                        y: rect.top,
                      });
                    }}
                    onMouseLeave={() => setHoveredUser(null)}
                  >{planningOnly&&evt.startMin>=cellStart&&<span className="availability-lane-role" aria-label={teamRoleLabel(role)}>{({player:'PL',coach:'CO',analyst:'AN',manager:'MG',sub:'SU',trialing:'TR',trial:'TR'})[role]||teamRoleLabel(role).slice(0,2).toUpperCase()}</span>}</div>
                );
              });

              // selection overlay range
              let effectiveRange = null;
              if (isDragging && dragStart && dragEnd && dragStart.day === day) {
                const [a, b] = [dragStart.minutes, dragEnd.minutes].sort((x, y) => x - y);
                effectiveRange = { day, from: a, to: b + 15 };
              } else if (popup && popup.day === day) {
                const r = getPopupRangeForDay(popup);
                if (r) effectiveRange = r;
              } else if (!isDragging && selectionRange?.day === day) {
                effectiveRange = selectionRange;
              }

              let overlay = null;
              if (effectiveRange) {
                const from = effectiveRange.from;
                const to = effectiveRange.to;
                const overlapStart = Math.max(from, cellStart);
                const overlapEnd = Math.min(to, cellEnd);
                const height = overlapEnd - overlapStart;

                if (height > 0) {
                  const topPct = ((overlapStart - cellStart) / 60) * 100;
                  const heightPct = (height / 60) * 100;
                  overlay = (
                    <div
                      className="selection-overlay"
                      style={{
                        top: `${topPct}%`,
                        height: `${heightPct}%`,
                      }}
                    />
                  );
                }
              }

              return (
                <div
                  key={key}
                  className={`avail-cell ${cls}`}
                  data-today={day===todayIndex||undefined}
                  role="button"
                  aria-label={planningOnly?`Schedule event ${getDisplayWeekStartLocal(weekStart,selectedTimeZone).plus({days:day}).toISODate()} ${mmToHHMM(hour*60)}–${mmToHHMM((hour+1)*60)}`:undefined}
                  tabIndex={editing ? -1 : 0}
                  onMouseDown={(e) => (editing ? null : handleMouseDown(day, hour, e))}
                  onMouseMove={(e) => handleMouseMove(day, hour, e)}
                  onKeyDown={(e) => {
                    if (editing) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      if(planningOnly)eventSelectionRef.current?.(day,hour*60,(hour+1)*60);
                      else handleMouseDown(day, hour, e);
                      e.preventDefault();
                    }
                  }}
                >
                  {userBlocks}
                  {!planningOnly&&showTeamEvents && bookingSegments.filter(segment => segment.day === day && segment.startMin < cellEnd && segment.endMin > cellStart).map((segment, index) => (
                    <div key={index} className="availability-booking-overlay" style={{
                      "--booking-color": getEventTagColor(segment.event.tag),
                      top: ((Math.max(segment.startMin, cellStart) - cellStart) / 60 * 100) + '%',
                      height: ((Math.min(segment.endMin, cellEnd) - Math.max(segment.startMin, cellStart)) / 60 * 100) + '%'
                    }}>
                      {segment.startMin >= cellStart && <button type="button" className="availability-booking-label"
                        onMouseDown={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}
                        onMouseEnter={e => showBookingDetails(segment.event, e.currentTarget)}
                        onMouseLeave={scheduleBookingDetailsClose}
                        onBlur={blurBookingDetails}
                        onFocus={e => showBookingDetails(segment.event, e.currentTarget)}
                        onClick={e => {e.stopPropagation(); showBookingDetails(segment.event, e.currentTarget);}}>
                        {segment.event.tag || 'Event'}
                      </button>}
                    </div>
                  ))}
                  {overlay}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      {planningOnly&&showTeamEvents&&<div className="availability-planning-layer" aria-label="Scheduled events on availability grid">{bookingLanes(bookingSegments).map((segment,index)=><button type="button" key={index} className="availability-booking-label availability-planning-card" data-availability-date={getDisplayWeekStartLocal(weekStart,selectedTimeZone).plus({days:segment.day}).toISODate()} style={{'--booking-color':getEventTagColor(segment.event.tag),'--event-day':segment.day,'--event-offset':.28+segment.lane*.70/segment.laneCount,'--event-width':.70/segment.laneCount,'--event-top':segment.startMin/60,'--event-duration':(segment.endMin-segment.startMin)/60}} onMouseDown={e=>e.stopPropagation()} onMouseEnter={e=>showBookingDetails(segment.event,e.currentTarget)} onMouseLeave={scheduleBookingDetailsClose} onFocus={e=>showBookingDetails(segment.event,e.currentTarget)} onBlur={blurBookingDetails} onClick={e=>{e.stopPropagation();showBookingDetails(segment.event,e.currentTarget);}}>{renderScheduledSummary?renderScheduledSummary(segment.event):<><strong>{segment.event.tag||'Event'}</strong><span>{mmToHHMM(segment.startMin)}–{mmToHHMM(segment.endMin)}</span></>}</button>)}</div>}
      </div>}

      {floatingPanel(!planningOnly&&bookingDetails && <aside className={`availability-booking-popover${renderScheduledEvent?' availability-booking-popover--rich':''}`} role="dialog" aria-label="Scheduled event details"
        onMouseEnter={keepBookingDetailsOpen} onMouseLeave={scheduleBookingDetailsClose}
        onFocus={keepBookingDetailsOpen} onBlur={blurBookingDetails}
        style={{left: bookingDetails.left, top: bookingDetails.top, "--booking-color": getEventTagColor(bookingDetails.event.tag)}} onKeyDown={e => {if (e.key === 'Escape') setBookingDetails(null);}}>
        <div className="availability-booking-header">
          <strong>{bookingDetails.event.tag || 'Team event'}</strong>
          <button type="button" className="availability-booking-open" aria-label="Open in calendar" title="Open in calendar" onClick={() => {
            const date = DateTime.fromISO(bookingDetails.event.startUTC).setZone(selectedTimeZone).toISODate();
            navigate('/home?view=events&' + new URLSearchParams({calendarDate: date, calendarTeam: teamId,
              calendarTimeZone: selectedTimeZone}).toString());
          }}><ExternalLink size={16} aria-hidden="true"/></button>
          <button type="button" className="availability-booking-close app-cancel-control" aria-label="Close event details" onClick={() => setBookingDetails(null)}>×</button>
        </div>
        {renderScheduledEvent?<div className="calendar-display overview-week availability-booking-card">{renderScheduledEvent(bookingDetails.event,DateTime.fromISO(bookingDetails.event.startUTC).setZone(selectedTimeZone))}</div>:<><p>{DateTime.fromISO(bookingDetails.event.startUTC).setZone(selectedTimeZone).toFormat('ccc d LLL, HH:mm')}–{DateTime.fromISO(bookingDetails.event.endUTC).setZone(selectedTimeZone).toFormat('ccc d LLL, HH:mm')} ({selectedTimeZone})</p>
        <strong>Participants</strong>
        <p>{bookingParticipantUids(bookingDetails.event, members).map(uid => redactEmail(members.find(member => member.uid === uid)?.name || 'Member')).join(', ') || (bookingDetails.event.participants || []).map(redactEmail).join(', ') || 'No participants selected'}</p></>}
      </aside>)}
      {/* Recurring action menu */}
      {floatingPanel(editing?.isRecurringInstance &&
        editing?.instanceDate &&
        editing?.repeat &&
        editing?.repeat !== 'none' &&
        !popup?.isOverride &&
        !popup?.isSplitFuture ? (
        <div
          ref={recurMenuRef}
          className="recurring-action-menu"
          style={floatingStyle}
        >
          {readOnly ? (
            <div className="calendar-error-message">{readOnlyMessage}</div>
          ) : (
            <>
              <button
                className="CalendarAvailability-Delete-This-Instance"
                onClick={() => handleDeleteInstance(editing)}
              >
                Delete This Instance
              </button>
              <button
                className="CalendarAvailability-Delete-All-Future"
                onClick={() => handleDeleteFuture(editing)}
              >
                Delete All Future
              </button>
              <button className="CalendarAvailability-Edit-This-Instance" onClick={handleEditInstance}>
                Edit This Instance
              </button>
              <button className="CalendarAvailability-Edit-All-Future" onClick={handleEditFuture}>
                Edit All Future
              </button>
            </>
          )}
          <button
            className="CalendarAvailability-cancel app-cancel-control"
            onClick={() => {
              setEditing(null);
              setPopup(null);
            }}
          >
            Cancel
          </button>
        </div>
      ) : popup ? (
        // New/Edit popup
        <div ref={popupRef} role="dialog" aria-label={editing?'Edit availability':'New availability'} className="availability-popup" data-embedded={embedded} style={floatingStyle}>
          <h4>{editing ? 'Edit Availability' : 'New Availability'}</h4>
          {embedded&&mode==='personal'&&!editing&&<label>Date:<input type="date" aria-label="Availability date" value={DateTime.fromJSDate(popup.startDate).toISODate()} onChange={event=>{
            const day=DateTime.fromISO(event.target.value);if(!day.isValid)return;
            const startDate=new Date(day.year,day.month-1,day.day,popup.startDate.getHours(),popup.startDate.getMinutes());
            const endDate=new Date(day.year,day.month-1,day.day,popup.endDate.getHours(),popup.endDate.getMinutes());
            if(endDate<=startDate)endDate.setDate(endDate.getDate()+1);
            setPopup(previous=>({...previous,day:day.weekday-1,startDate,endDate}));setSelectionRange(null);
          }}/></label>}

          <label>
            Start:
            <div className="select-wrapper">
              <select
                value={fmt(popup.startDate)}
                onChange={(e) => {
                  const [h, m] = e.target.value.split(':').map(Number);
                  const d = new Date(popup.startDate);
                  d.setHours(h, m, 0, 0);
                  setPopup((p) => ({ ...p, startDate: d }));
                }}
              >
                {generateTimeOptions(true).map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </label>

          <label>
            End:
            <div className="select-wrapper">
              <select
                value={fmtEnd(popup.startDate, popup.endDate)}
                onChange={(e) => {
                  const val = e.target.value;
                  const d = new Date(popup.endDate);

                  // ✅ FIX: allow 24:00 by rolling to next day 00:00
                  if (val === '24:00') {
                    d.setHours(0, 0, 0, 0);
                    d.setDate(d.getDate() + 1);
                  } else {
                    const [h, m] = val.split(':').map(Number);
                    d.setHours(h, m, 0, 0);
                  }

                  setPopup((p) => ({ ...p, endDate: d }));
                }}
              >
                {generateTimeOptions(true).map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </label>

          <label>
            Repeat:
            <div className="select-wrapper">
              <select
                name="repeat"
                value={popup.repeat}
                onChange={(e) => {
                  const nextRepeat = e.target.value;

                  setPopup((p) => {
                    if (nextRepeat !== 'none') {
                      const hasEnd =
                        typeof p.repeatEndDate === 'string' && p.repeatEndDate.trim().length > 0;
                      const nextEnd = hasEnd ? p.repeatEndDate : defaultRepeatEndDate4Months(p.startDate);
                      return { ...p, repeat: nextRepeat, repeatEndDate: nextEnd };
                    }
                    return { ...p, repeat: nextRepeat, repeatEndDate: '' };
                  });
                }}
              >
                <option value="none">None</option>
                <option value="weekly">Every week</option>
                <option value="biweekly">Every 2 weeks</option>
              </select>
            </div>
          </label>

          {popup.repeat !== 'none' && (
            <label>
              Until:
              <input
                type="date"
                value={popup.repeatEndDate}
                onChange={(e) => setPopup((p) => ({ ...p, repeatEndDate: e.target.value }))}
              />
            </label>
          )}

          <div className="Availability-popup-buttons">
            {readOnly ? (
              <div className="calendar-error-message">{readOnlyMessage}</div>
            ) : (
              <>
                <button onClick={async()=>{try{await handleConfirm();setStatusAlert({type:'success',message:'Availability saved.'});}catch(error){setStatusAlert({type:'error',message:error.message||'Could not save availability.'});}}}>{editing ? 'Save Changes' : 'Confirm'}</button>
                {editing && (
                  <button type="button" className="delete-button" onClick={handleDelete}>
                    Delete
                  </button>
                )}
              </>
            )}
            <button className={'app-cancel-control'} onClick={handleCancel}>Cancel</button>
          </div>
        </div>
      ) : null)}

      {hoveredUser && (
        <div
          className="availability-tooltip"
          style={{
            top: hoveredUser.y + 'px',
            left: hoveredUser.x + 'px',
          }}
        >
          {hoveredUser.name}{hoveredUser.role&&<small style={{color:hoveredUser.roleColor}}> · {hoveredUser.role}</small>}
        </div>
      )}
    </div>
  );
}

function AvailabilityToolsPortal({target,children}) {return target?createPortal(children,target):children;}
